'use client';

import dynamic from 'next/dynamic';

import * as React from 'react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useContext,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import ImageNotFound from './imageNotFound';
import { ContentModerationPreviewSwitcher } from './content-moderation-preview-switcher';
import { CaptureViewSwitcher } from './capture-view-switcher';
import { MediaDisplayCaptureActions } from './mediaDisplayCaptureActions';
import { MediaDisplayCaptureGuides } from './mediaDisplayCaptureGuides';
import { MediaDisplayCaptureMask } from './mediaDisplayCaptureMask';
import { MediaDisplayZoomControls } from './mediaDisplayZoomControls';
import { Pane } from '@tranquility-ai/theme-components/pane';
import '@tranquility-ai/theme-components/pane.css';
import { MediaSource } from '@/shared/types';
import MediaPlayer from './media/player';
import {
  FullscreenImageViewport,
  FullscreenZoomableViewport,
  IframeZoomViewport,
} from './media/fullscreen-viewports';
import { resolveTranscriptPosition } from './media/transcriptPosition';
import {
  FileAudioIcon,
  FileTextIcon,
  FileHtmlIcon,
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  ArrowsOutSimpleIcon,
  ArrowsInSimpleIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  XCircleIcon,
  XIcon,
  TextTIcon,
  TableIcon,
  CropIcon,
  FrameCornersIcon,
  TrashIcon,
  SpinnerGapIcon,
  BracketsCurlyIcon,
} from "@phosphor-icons/react";
import { getCurrentFileInfo, useWindowSize } from '@/shared/clientFunctions';
import { checkSourceContentType, encodeEvidenceLockerStyleProxyUriSegment, extractPathAfterBulk, fileExtensionFromStoragePath, formatTimeIndex, getMediaSourceUrlKey, getMediaType, isArchiveMemberSearchablePath, isMetaBundleFilename, isMetaFile, junkdrawerDocumentApiPathFromUrl, mediaPaneIdentityPath, mediaPaneTitleLabel, paintTranscriptMediaContext, parseCitationPageFromMedia, resolveFileInfoMediaDisplayUrl, resolveFullS3Url, resolveMediaTranscriptUrl, resolvePreferredMediaDisplayForSource, resolveSameOriginCaptureSrcForVideo, resolveTranscriptUrlFromFileInfo, shouldShowTranscodedMediaRetryNotice } from '@/shared/functions';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { detachCaptureVideo, loadSameOriginVideoFrame } from '@/shared/loadSameOriginVideoFrame';
import { isSpreadsheetMediaItem, shouldBoundSpreadsheetInFillContainer } from '@/shared/media-preview-layout';
import { FileTypes } from '@/shared/constants';
import MetaFileDisplay from './metaPreviewViewer';
import AnswerStyleMarkdown from './answerStyleMarkdown';
import {
  citationJsonMediaBaseUrl,
  enhanceCitationMarkdownContent,
  resolveCitationMarkdownExcerpt,
} from '@/shared/enhanceCitationMarkdown';
import { citationViewDetailsText } from '@/shared/citationScrollNeedle';
import { FullScreenContext, FullscreenTransformActionsContext, FullscreenTransformStateContext, ToastContext, useReactiveFeatureFlagValue } from '@/app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import { useJsonMediaDisplayEnabled, useMediaPlayerTransitionEnabled } from '@/hooks/useMediaPlayerTransition';
import {
  IframeViewer,
  YOUTUBE_IFRAME_ALLOW,
} from '@tranquility-ai/theme-components/iframe-viewer';
import '@tranquility-ai/theme-components/iframe-viewer.css';
import {
  FULLSCREEN_ZOOM_MAX_STEPS,
  applyImmediateFullscreenZoomPreview,
  getFullscreenMinZoomLevel,
  imagePaintRotationDeg,
  resolveImagePaintRotation,
  shouldShowMediaRotateControls,
  zoomLevelFromPercent,
  zoomPercentFromLevel,
} from '@/lib/fullscreen-zoom';
import { fullscreenSettledZoomRef } from '@/components/fullscreen-transform-provider';
import { fetchJunkdrawerMboxEmail } from '@/shared/cors-junkdrawer-api';
import { rememberViewingPageForMedia, rememberPdfSearchForMedia, stampViewingPageScope, type PdfSearchHandoffState } from '@/components/ui/pdfViewerPageHandoff';
import {
  displayedMediaChoiceToCommit,
  getRememberedCaptureView,
  mirrorCaptureViewState,
  rememberCaptureView,
  resolveCaptureViewOnFullscreenExit,
  resolveInitialViewCapture,
} from '@/components/ui/captureViewHandoff';
import {
  getMboxEmailIdentityKey,
  getMediaDisplayCaptureIdentityKey,
  getMediaDisplayClipIdentityKey,
  isMbox,
  mboxMemberAlreadyResolved,
  parseMediaContextEmailIndex,
} from '@/components/ui/media/mediaIdentityKeys';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { Button } from '@tranquility-ai/theme-components/button';
import { Chip, ChipGroup } from '@tranquility-ai/theme-components/chip';
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import { cn } from '@/lib/utils';
import { mediaIconButtonProps } from './media/media-icon-button';
import { CsamDetectionPill } from './media/csam-detection-pill';
import { MediaFlagMenu } from './media/media-flag-menu';
import {
  isImageMedia,
  resolveBoundSafePreview,
  resolveEffectiveContentModerationType,
  resolveFlagFileUrl,
  shouldShowCsamSafePreview,
  shouldShowFlagControl,
} from './media/media-flag-rules';
import {
  MediaObfuscationPending,
  type MediaObfuscationState,
} from './media/media-obfuscation-pending';
import { useManualObfuscationJobStatus } from '@/hooks/useManualObfuscationJobStatus';
import {
  isLocalObfuscationBaselineReviewed,
  obfuscationJobGeneration,
} from '@/lib/manual-obfuscation-job';
import { usePollObfuscatedPreview } from '@/hooks/usePollObfuscatedPreview';
import { ToastLevel } from '@/components/ui/toast';
import html2canvas from 'html2canvas-pro';
import type { CaptureRegion } from './mediaDisplayCaptureModal';
import {
  capturePdfRegionCanvasAsync,
  isPdfCaptureRoot,
} from '@/shared/pdfCanvasThumbnail';
import {
  canvasHasVisiblePixels,
  mediaPreviewShowsCapture,
  mediaTypeSupportsThumbnailCapture,
  normalizeCaptureRegion as sharedNormalizeCaptureRegion,
  normalizeRotationDeg,
  persistableCaptureRef,
  resolveCaptureDisplaySrc,
  resolveCapturePersistSrc,
  resolveCaptureRemovalState,
  resolvePersistedCaptureRef,
  shouldAutoSaveCaptureOnCommit,
  shouldShowCaptureRegionDialog,
  isExplicitlyClearedCapture,
  videoCropFromSelection,
  captureCanvasSize,
  CAPTURE_WEBP_QUALITY,
  type NormalizedCaptureRegion,
} from '@/shared/captureRegion';
import {
  copyCaptureCanvasToClipboard,
  shouldBlockCaptureClipboardForOriginalCsam,
} from '@/shared/copyCaptureToClipboard';
import {
  useOptionalToolsSharedCaseData,
  type ToolsSharedCaptureEntry,
} from '@/components/tools-shared-case-data';
import {
  buildCaptureFavoriteEntry,
  flushQueuedCaptureFavorites,
  persistCaptureFavorite,
} from '@/shared/captureFavorites';
import {
  isCaptureSaveLogEnabled,
  logCaptureSave,
  startCaptureSave,
  type CaptureSaveLog,
} from '@/shared/captureSaveLog';
import { resolveCompactPreview, shouldShowCompactCaptureStill } from '@/shared/timelineThumbnails';
import { MediaDisplayErrorBoundary } from './mediaDisplayErrorBoundary';
import { useToolsDrawerCaptureApplyNonce } from '@/components/tools-drawer';
import {
  uploadUserImage,
  resolveUserImageUrl,
  parseStagedCaptureFromLocalStorage,
  stagedCaptureImageRef,
  hasStagedCapture,
  originBindingFromMedia,
  resolveUserImageUploadCaseId,
  stagedCaptureBelongsToMedia,
  type StagedCaptureData,
} from '@/shared/userImageStorage';

const CAPTURE_TOOLTIP_CLASSNAMES = {
  base: 'before:bg-tooltip-bg z-[99999999]',
  content: 'flex flex-row items-center justify-center w-[200px] wrap',
  arrow: 'text-white',
} as const;

const MediaDisplayCaptureModal = dynamic(
  () => import('./mediaDisplayCaptureModal').then((m) => m.MediaDisplayCaptureModal),
  { ssr: false, loading: () => null },
);

const XlsxViewer = dynamic(() => import('@/components/xlsx-viewer/XlsxViewer'), { ssr: false });
const ThemeSpreadsheetViewer = dynamic(
  () => import('@/components/xlsx-viewer/ThemeSpreadsheetViewer'),
  { ssr: false },
);

const PdfViewer = dynamic(() => import('./pdfViewer'), { ssr: false });
const PlainTextViewer = dynamic(() => import('@/components/plain-text-viewer/PlainTextViewer'), { ssr: false });
const MarkdownViewer = dynamic(() => import('@/components/markdown-viewer/MarkdownViewer'), { ssr: false });
const JsonViewer = dynamic(() => import('@/components/json-viewer/AppJsonViewer'), { ssr: false });

const hasStillFrameImage = (media: MediaSource | null | undefined): boolean => {
  return [media?.mediaContext, media?.sourceContext].some((context) => {
    if (typeof context?.stillFrame === 'string' && context.stillFrame.startsWith('data:image/')) {
      return true;
    }
    const storedRef = context?.canvas?.stillFrameImage;
    // Persistable still frames are junkdrawer ``s3://`` / proxy refs, not only
    // legacy inlined data URLs.
    return typeof storedRef === 'string' && storedRef.trim() !== '';
  });
};

/**
 * True when a mouse event reached the media box only by bubbling out of a portaled
 * child. The capture modal is a JSX child of the box but renders under
 * `document.body`, and React propagates synthetic events along the component tree
 * rather than the DOM, so its clicks still arrive at the box's drag handlers.
 * Drag-select has to ignore them: a click on the modal's Save button would
 * otherwise open and commit a zero-size selection, which unmounts the modal
 * before the `click` can reach the button and the save never runs.
 */
const isFromPortaledChild = (e: React.MouseEvent<HTMLDivElement>) =>
  !e.currentTarget.contains(e.target as Node);

const normalizedCaptureRegion = (cap: CaptureRegion): NormalizedCaptureRegion =>
  sharedNormalizeCaptureRegion(cap);

const axisAlignedFromDomRect = (rect: DOMRect) => ({
  left: rect.left,
  top: rect.top,
  right: rect.right,
  bottom: rect.bottom,
  width: rect.width,
  height: rect.height,
});

const drawCurrentVideoFrame = (
  video: HTMLVideoElement,
  crop: { xRatio: number; yRatio: number; widthRatio: number; heightRatio: number },
): HTMLCanvasElement | null => {
  if (!video.videoWidth || !video.videoHeight || !crop.widthRatio || !crop.heightRatio) {
    return null;
  }

  const frameCanvas = document.createElement('canvas');
  const frameContext = frameCanvas.getContext('2d');
  if (!frameContext) {
    return null;
  }

  frameCanvas.width = Math.max(1, Math.round(crop.widthRatio * video.videoWidth));
  frameCanvas.height = Math.max(1, Math.round(crop.heightRatio * video.videoHeight));
  frameContext.drawImage(
    video,
    crop.xRatio * video.videoWidth,
    crop.yRatio * video.videoHeight,
    crop.widthRatio * video.videoWidth,
    crop.heightRatio * video.videoHeight,
    0,
    0,
    frameCanvas.width,
    frameCanvas.height,
  );
  frameCanvas.toDataURL('image/webp');
  return frameCanvas;
};

const drawVideoFrameOnCaptureCanvas = async (
  root: HTMLElement,
  captureCanvas: HTMLCanvasElement,
  cap: CaptureRegion,
  fallbackSrc: string | undefined,
  rotationDeg = 0,
) => {
  const video = root.querySelector('video');
  if (!video || !video.videoWidth || !video.videoHeight) {
    return;
  }

  const normalizedCap = normalizedCaptureRegion(cap);
  if (!normalizedCap.width || !normalizedCap.height) {
    return;
  }

  const cropRatios = videoCropFromSelection({
    rootRect: axisAlignedFromDomRect(root.getBoundingClientRect()),
    videoAabb: axisAlignedFromDomRect(video.getBoundingClientRect()),
    mediaWidth: video.videoWidth,
    mediaHeight: video.videoHeight,
    selection: normalizedCap,
    rotationDeg,
  });
  if (!cropRatios) {
    return;
  }

  const isCanvasTaintError = (error: unknown) =>
    error instanceof DOMException
      ? error.name === 'SecurityError'
      : error instanceof Error && error.name === 'SecurityError';

  let frameCanvas: HTMLCanvasElement | null = null;
  try {
    frameCanvas = drawCurrentVideoFrame(video, cropRatios);
  } catch (error) {
    if (!isCanvasTaintError(error) || !fallbackSrc) {
      throw error;
    }
  }

  let fallbackVideo: HTMLVideoElement | null = null;
  if (!frameCanvas && fallbackSrc) {
    try {
      fallbackVideo = await loadSameOriginVideoFrame(fallbackSrc, video.currentTime);
      frameCanvas = drawCurrentVideoFrame(fallbackVideo, cropRatios);
    } catch (error) {
      console.warn(
        'Same-origin video capture fallback failed:',
        error instanceof Error ? error.message : error,
      );
    } finally {
      detachCaptureVideo(fallbackVideo);
    }
  }

  if (!frameCanvas) {
    return;
  }

  const context = captureCanvas.getContext('2d');
  if (!context) {
    return;
  }
  const scaleX = normalizedCap.width > 0 ? captureCanvas.width / normalizedCap.width : 1;
  const scaleY = normalizedCap.height > 0 ? captureCanvas.height / normalizedCap.height : 1;
  const destX = cropRatios.destX * scaleX;
  const destY = cropRatios.destY * scaleY;
  const destWidth = Math.max(1, cropRatios.destWidth * scaleX);
  const destHeight = Math.max(1, cropRatios.destHeight * scaleY);
  const rotation = normalizeRotationDeg(rotationDeg);
  context.save();
  context.translate(destX + destWidth / 2, destY + destHeight / 2);
  if (rotation) {
    context.rotate((rotation * Math.PI) / 180);
  }
  const drawWidth = rotation === 90 || rotation === 270 ? destHeight : destWidth;
  const drawHeight = rotation === 90 || rotation === 270 ? destWidth : destHeight;
  context.drawImage(frameCanvas, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  context.restore();
};

/**
 * Composite a same-origin iframe document (``html`` via ``/api/proxy/``) into
 * the html2canvas crop. html2canvas cannot see into iframes, so we snapshot
 * ``contentDocument`` separately and draw the intersecting slice.
 */
const drawIframeDocumentOnCaptureCanvas = async (
  root: HTMLElement,
  captureCanvas: HTMLCanvasElement,
  cap: NormalizedCaptureRegion,
) => {
  const iframe = root.querySelector('iframe');
  if (!iframe) return;

  let doc: Document | null = null;
  try {
    doc = iframe.contentDocument;
  } catch {
    return;
  }
  if (!doc?.documentElement) return;

  const rootRect = axisAlignedFromDomRect(root.getBoundingClientRect());
  const iframeRect = axisAlignedFromDomRect(iframe.getBoundingClientRect());
  const selection = {
    left: rootRect.left + cap.x,
    top: rootRect.top + cap.y,
    right: rootRect.left + cap.x + cap.width,
    bottom: rootRect.top + cap.y + cap.height,
  };
  const intersectLeft = Math.max(selection.left, iframeRect.left);
  const intersectTop = Math.max(selection.top, iframeRect.top);
  const intersectRight = Math.min(selection.right, iframeRect.right);
  const intersectBottom = Math.min(selection.bottom, iframeRect.bottom);
  const destWidth = intersectRight - intersectLeft;
  const destHeight = intersectBottom - intersectTop;
  if (destWidth <= 0 || destHeight <= 0) return;

  const destX = intersectLeft - selection.left;
  const destY = intersectTop - selection.top;
  const zoomScale =
    iframe.offsetWidth > 0 ? iframeRect.width / iframe.offsetWidth : 1;
  const win = iframe.contentWindow;
  const scrollX = win?.scrollX ?? doc.documentElement.scrollLeft ?? 0;
  const scrollY = win?.scrollY ?? doc.documentElement.scrollTop ?? 0;
  const sourceX = (intersectLeft - iframeRect.left) / zoomScale + scrollX;
  const sourceY = (intersectTop - iframeRect.top) / zoomScale + scrollY;
  const sourceWidth = destWidth / zoomScale;
  const sourceHeight = destHeight / zoomScale;

  let innerCanvas: HTMLCanvasElement;
  try {
    innerCanvas = await html2canvas(doc.documentElement, {
      x: sourceX,
      y: sourceY,
      width: Math.max(1, sourceWidth),
      height: Math.max(1, sourceHeight),
      backgroundColor: '#fff',
      scale: 1,
      logging: false,
      windowWidth: Math.max(doc.documentElement.scrollWidth, 1),
      windowHeight: Math.max(doc.documentElement.scrollHeight, 1),
    });
  } catch (error) {
    console.warn(
      'Iframe thumbnail capture failed:',
      error instanceof Error ? error.message : error,
    );
    return;
  }

  const context = captureCanvas.getContext('2d');
  if (!context) return;
  const outerScaleX = cap.width > 0 ? captureCanvas.width / cap.width : 1;
  const outerScaleY = cap.height > 0 ? captureCanvas.height / cap.height : 1;
  context.drawImage(
    innerCanvas,
    destX * outerScaleX,
    destY * outerScaleY,
    destWidth * outerScaleX,
    destHeight * outerScaleY,
  );
};

const captureVideoRegionCanvas = async (
  root: HTMLElement,
  cap: CaptureRegion,
  fallbackSrc: string | undefined,
  rotationDeg = 0,
): Promise<HTMLCanvasElement | null> => {
  const video = root.querySelector('video');
  if (!video) {
    return null;
  }

  const normalizedCap = normalizedCaptureRegion(cap);
  const crop = videoCropFromSelection({
    rootRect: axisAlignedFromDomRect(root.getBoundingClientRect()),
    videoAabb: axisAlignedFromDomRect(video.getBoundingClientRect()),
    mediaWidth: video.videoWidth,
    mediaHeight: video.videoHeight,
    selection: normalizedCap,
    rotationDeg,
  });
  if (!crop) {
    return null;
  }

  const canvas = document.createElement('canvas');
  const intrinsicScaleX =
    crop.sourceWidth > 0 ? (crop.widthRatio * video.videoWidth) / crop.sourceWidth : 1;
  const intrinsicScaleY =
    crop.sourceHeight > 0 ? (crop.heightRatio * video.videoHeight) / crop.sourceHeight : 1;
  const destSize = captureCanvasSize(
    normalizedCap.width,
    normalizedCap.height,
    intrinsicScaleX,
    intrinsicScaleY,
  );
  canvas.width = destSize.width;
  canvas.height = destSize.height;
  try {
    await drawVideoFrameOnCaptureCanvas(root, canvas, normalizedCap, fallbackSrc, rotationDeg);
  } catch (error) {
    console.warn(
      'Video region capture failed:',
      error instanceof Error ? error.message : error,
    );
    return null;
  }

  return canvasHasVisiblePixels(canvas) ? canvas : null;
};

function isMediaDisplayClipAligned(
  item: MediaSource | undefined,
  clipKey: string,
): boolean {
  return !!item && !!clipKey && getMediaDisplayClipIdentityKey(item) === clipKey;
}

/** Fields edited in-place (trim, transcript layout, etc.) — merge by ``lastUpdated``, not prop order. */
const USER_EDITABLE_MEDIA_CONTEXT_KEYS = [
  'startTime',
  'endTime',
  'originalStartTime',
  'originalEndTime',
  'stillFrame',
  'rotation',
  'capture',
  'canvas',
  'showTranscript',
  'showTranscriptMarkers',
  'autoScroll',
  'transcriptPosition',
] as const;

/** Always flushed on fullscreen exit when present (trim / capture / rotation). */
const FLUSH_ALWAYS_MEDIA_CONTEXT_KEYS = [
  'startTime',
  'endTime',
  'originalStartTime',
  'originalEndTime',
  'stillFrame',
  'rotation',
  'capture',
  'canvas',
] as const;

/**
 * Layout keys flushed only when they differ from the fullscreen-entry baseline.
 * Maximize may stamp ``transcriptPosition`` onto the fullscreen copy for remount
 * continuity; that stamp must not look like a user edit on exit.
 */
const FLUSH_LAYOUT_MEDIA_CONTEXT_KEYS = [
  'showTranscript',
  'showTranscriptMarkers',
  'autoScroll',
  'transcriptPosition',
] as const;

function snapshotLayoutMediaContext(
  ctx: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const snap: Record<string, unknown> = {};
  if (!ctx) return snap;
  for (const key of FLUSH_LAYOUT_MEDIA_CONTEXT_KEYS) {
    if (ctx[key] !== undefined) {
      snap[key] = ctx[key];
    }
  }
  return snap;
}

/**
 * Shared across owner + app-level MediaDisplay instances (portal remount).
 * Set on maximize enter (after remount stamps); cleared after flush / exit.
 */
let fullscreenLayoutBaseline: Record<string, unknown> | null = null;

/**
 * Media row just written by Use Full Media / Use Capture, or by saving the
 * switcher on close. Every MediaDisplay instance flushes on fullscreen exit;
 * without this they would write the maximize-time copy and put the capture back.
 */
let committedFullscreenMediaOverride: MediaSource | null = null;

function setFullscreenLayoutBaseline(
  ctx: Record<string, unknown> | null | undefined,
): void {
  fullscreenLayoutBaseline = snapshotLayoutMediaContext(ctx);
}

function clearFullscreenLayoutBaseline(): void {
  fullscreenLayoutBaseline = null;
}

/**
 * Merge parent ``mediaContext`` into local without clobbering fresher in-place edits.
 * After fullscreen exit the owner may flush trim into ``itemToUse`` while ``mediaItem``
 * props are still one render behind — plain ``{ ...local, ...prop }`` would revert trim.
 */
function mergeMediaContextPreservingFreshLocalEdits(
  localCtx: Record<string, unknown>,
  propCtx: Record<string, unknown>,
): Record<string, unknown> {
  const localUpdated = typeof localCtx.lastUpdated === 'number' ? localCtx.lastUpdated : 0;
  const propUpdated = typeof propCtx.lastUpdated === 'number' ? propCtx.lastUpdated : 0;

  const nextCtx: Record<string, unknown> = {
    ...localCtx,
    ...propCtx,
    previewUrl: localCtx.previewUrl ?? propCtx.previewUrl,
    transcriptUrl: localCtx.transcriptUrl ?? propCtx.transcriptUrl,
    transcript_url: localCtx.transcript_url ?? propCtx.transcript_url,
  };

  for (const key of USER_EDITABLE_MEDIA_CONTEXT_KEYS) {
    const hasLocal = Object.prototype.hasOwnProperty.call(localCtx, key);
    const hasProp = Object.prototype.hasOwnProperty.call(propCtx, key);
    if (!hasLocal && !hasProp) continue;

    const localVal = localCtx[key];
    const propVal = propCtx[key];
    if (localVal === propVal) continue;

    if (localUpdated >= propUpdated && hasLocal) {
      nextCtx[key] = localVal;
    } else if (hasProp) {
      nextCtx[key] = propVal;
    }
  }

  const mergedUpdated = Math.max(localUpdated, propUpdated);
  if (mergedUpdated > 0) {
    nextCtx.lastUpdated = mergedUpdated;
  }

  return nextCtx;
}

function mediaContextCanvasEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  return (
    aObj.stillFrame === bObj.stillFrame &&
    aObj.stillFrameImage === bObj.stillFrameImage &&
    aObj.image === bObj.image
  );
}

/** Chip switch within the same file: parent segment context wins (not lastUpdated merge). */
function mergeMediaContextForSegmentSelection(
  localCtx: Record<string, unknown>,
  propCtx: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...localCtx,
    ...propCtx,
    previewUrl: localCtx.previewUrl ?? propCtx.previewUrl,
    transcriptUrl: localCtx.transcriptUrl ?? propCtx.transcriptUrl,
    transcript_url: localCtx.transcript_url ?? propCtx.transcript_url,
  };
}

/** True when this instance opened fullscreen for ``fullscreenItem`` (not the app-level viewer). */
function isFullscreenOwnerMediaDisplay(
  localMedia: MediaSource | undefined,
  fullscreenItem: MediaSource | null | undefined,
  isAppFullscreenViewer: boolean,
  hasUpdateMediaContext: boolean,
): boolean {
  if (isAppFullscreenViewer || !fullscreenItem || !hasUpdateMediaContext || !localMedia) {
    return false;
  }
  const ownerClipKey = getMediaDisplayClipIdentityKey(localMedia);
  const fullscreenClipKey = getMediaDisplayClipIdentityKey(fullscreenItem);
  return !!ownerClipKey && ownerClipKey === fullscreenClipKey;
}

/** Clip identity for gating ``itemToUse`` / ``displayItem`` — in fullscreen, follow context item. */
function getMediaDisplayClipSourceItem(
  mediaItem: MediaSource | undefined,
  isFullscreen: boolean,
  fullscreenMediaItem: MediaSource | null | undefined,
  isLinkedMedia: boolean | undefined,
): MediaSource | undefined {
  if (isLinkedMedia) {
    return mediaItem;
  }
  if (isFullscreen && fullscreenMediaItem) {
    return fullscreenMediaItem;
  }
  return mediaItem;
}

const isXmlFile = (item: MediaSource | undefined) => {
  if (!item) return false;
  const url = item.originalUrl || item.srcToUse || item.mediaUrl || item.mediaSrc || '';
  return fileExtensionFromStoragePath(url) === 'xml';
};

const textContentUrl = (item: MediaSource | undefined) =>
  item?.srcToUse || item?.previewFilePath || item?.mediaUrl || item?.mediaSrc || item?.originalUrl || '';

const isMarkdownPreview = (item: MediaSource | undefined) => {
  if (!item) return false;
  const pathOnly = textContentUrl(item).split(/[?#]/)[0].toLowerCase();
  return pathOnly.endsWith('.md') || pathOnly.endsWith('.markdown');
};

const isPlainTextFile = (item: MediaSource | undefined) => {
  if (!item || item.mediaType !== 'text' || !item.srcToUse) return false;
  if (isMetaFile(item) || isXmlFile(item)) return false;
  if (isMarkdownPreview(item) || isSpreadsheetMediaItem(item)) return false;
  return true;
};

const getSrcToUse = (srcItem: MediaSource | undefined) => {
  // Single source of truth: defer to ``resolvePreferredMediaDisplayForSource`` so
  // the URL chosen here (after file-info loads) matches the one used to seed
  // ``srcToUse`` on the original MediaSource. In particular this honors the
  // container rule (dispatchedFilePath before previewFilePath) instead of
  // unconditionally preferring previewFilePath, which previously flipped a
  // container citation from the dispatched/extracted file to the preview
  // thumbnail once file-info resolved.
  if (!srcItem) return undefined;
  const preferred = resolvePreferredMediaDisplayForSource(srcItem);
  const candidates = [
    preferred.mediaUrl,
    resolveFileInfoMediaDisplayUrl(srcItem.fileInfo, ''),
    srcItem.s3Url,
    srcItem.mediaUrl,
    srcItem.mediaSrc,
    srcItem.srcToUse,
    // Intentionally omit originalUrl / sourceUrl / sourceFilePath when they are
    // virtual ``…/archive.zip/inner`` searchable paths — those 404 on /api/proxy.
    // Top-level ``data/bulk/…/file.png`` paths are fine (no container segment).
    srcItem.originalUrl,
    srcItem.sourceUrl,
    srcItem.sourceFilePath,
  ];
  for (const candidate of candidates) {
    const url = typeof candidate === 'string' ? candidate.trim() : '';
    if (!url) continue;
    if (isArchiveMemberSearchablePath(url)) continue;
    return url;
  }
  return undefined;
};

/**
 * Meta bundle row (`isMetaFile`) may preview a non-bundle URL; re-derive
 * ``mediaType`` from the preview path itself rather than the bundle's
 * fileInfo. The legacy implementation constructed a synthetic ``SourceInfo``
 * with the preview URL stuffed into ``sourceContentType`` to coerce
 * ``getMediaType``'s mime-type branch; with FileSummary we can just call the
 * path-only resolver directly.
 */
function mediaObjectForMetaPreviewMismatch(item: MediaSource): MediaSource {
  const src = item.srcToUse?.trim();
  if (!src || !isMetaFile(item) || isMetaBundleFilename(src)) {
    return item;
  }
  const previewMediaSource: MediaSource = {
    ...item,
    previewFilePath: '',
    mediaUrl: src,
    mediaSrc: src,
    sourceUrl: src,
    mediaType: 'unknown',
    // Don't inherit the bundle's fileInfo — the preview URL is a different file.
    fileInfo: null,
  };

  const derivedType = getMediaType(previewMediaSource);
  if (derivedType === item.mediaType) {
    return item;
  }
  return {
    ...previewMediaSource,
    mediaType: derivedType,
  };
}

export type FetchMboxSession = {
  cellId: string | null;
  accessToken: string | null;
};

export const fetchMboxData = async (
  sourceFilePath: string | undefined,
  emailUrl: string | undefined,
  session: FetchMboxSession,
  signal?: AbortSignal,
  emailIndexOverride?: number,
) => {
  try {
    if (!sourceFilePath) {
      return null;
    }
    if (!session.cellId || !session.accessToken) {
      return null;
    }
    let emailIndex = 0;
    if (emailIndexOverride !== undefined && Number.isFinite(emailIndexOverride)) {
      emailIndex = emailIndexOverride;
    } else if (emailUrl) {
      const parsedEmailIndex = emailUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
      if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
        emailIndex = parseInt(parsedEmailIndex);
      }
    }
    const data = await fetchJunkdrawerMboxEmail<any>({
      cellId: session.cellId,
      accessToken: session.accessToken,
      s3Url: sourceFilePath,
      emailIndex,
      signal,
    });
    return data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return null;
    }
    return null;
  }
}

const rotatableMediaTypes = ['image', 'pdf', 'video', 'docx', 'pptx'];

/** Media types that receive zoom controls in fullscreen (audio / thumbnail modes excluded). */
function isFullscreenZoomableMedia(
  displayItem: MediaSource | undefined,
  isCapture: boolean,
  viewAsCsvLike: boolean,
  isThumbnailMode: boolean,
): boolean {
  if (!displayItem || isThumbnailMode) return false;
  if (isCapture) return true;
  const type = displayItem.mediaType || '';
  if (
    [
      'image',
      'pdf',
      'docx',
      'pptx',
      'spreadsheet',
      'markdown',
      'text',
      'html',
      'json',
      'youtube',
      'website',
      'video',
    ].includes(type)
  ) {
    return true;
  }
  if (displayItem.srcToUse && isMetaFile(displayItem)) return true;
  if ((type === 'text' || type === 'markdown') && (isSpreadsheetMediaItem(displayItem) || viewAsCsvLike)) {
    return true;
  }
  return false;
}

export type { CaptureRegion } from './mediaDisplayCaptureModal';

export type CapturedDataObject = StagedCaptureData;

export const maxDuration = 300; // allow the long requests for huge files

/**
 * Identity of the underlying source that stays stable across obfuscated/original variants. Uses
 * the canonical source path (`originalUrl`/`sourceUrl`) first because `sourceFilePath` resolves to
 * the variant-specific display URL and therefore differs between the obfuscated and original
 * previews. Used to match a source card to the item currently shown in the app-level fullscreen
 * viewer.
 */
function mediaSourceStableKey(m: MediaSource | null | undefined): string {
  const s = m as any;
  return (s?.originalUrl || s?.sourceUrl || s?.sourceFilePath || '') as string;
}

/**
 * Signature of the variant-specific fields (which differ between the obfuscated and original
 * previews), used to detect when the displayed fullscreen variant needs to swap.
 */
function mediaSourceVariantKey(m: MediaSource | null | undefined): string {
  const s = m as any;
  return JSON.stringify({
    p: s?.previewFilePath ?? null,
    b: s?.bypassCm ?? null,
    s: s?.srcToUse ?? null,
    u: s?.mediaUrl ?? null,
    c: s?.mediaSrc ?? null,
  });
}

/**
 * Derives an obfuscated WebP data URL from a captured canvas: downscale to ~16px on the
 * longest side then upscale with smoothing disabled (pixelation) plus a blur pass. Used for
 * content-moderation-flagged captures so a non-recoverable obfuscated variant can be stored.
 * Returns ``undefined`` if a 2D context cannot be obtained.
 */
function deriveObfuscatedCaptureDataUrl(source: HTMLCanvasElement): string | undefined {
  const srcW = source.width;
  const srcH = source.height;
  if (!srcW || !srcH) return undefined;

  const longest = Math.max(srcW, srcH);
  const targetMax = 16;
  const scale = Math.min(1, targetMax / longest);
  const smallW = Math.max(1, Math.round(srcW * scale));
  const smallH = Math.max(1, Math.round(srcH * scale));

  const small = document.createElement('canvas');
  small.width = smallW;
  small.height = smallH;
  const smallCtx = small.getContext('2d');
  if (!smallCtx) return undefined;
  smallCtx.drawImage(source, 0, 0, smallW, smallH);

  const out = document.createElement('canvas');
  out.width = srcW;
  out.height = srcH;
  const outCtx = out.getContext('2d');
  if (!outCtx) return undefined;
  outCtx.imageSmoothingEnabled = false;
  try {
    outCtx.filter = 'blur(8px)';
  } catch {
    // filter unsupported; pixelation alone still obfuscates
  }
  outCtx.drawImage(small, 0, 0, smallW, smallH, 0, 0, srcW, srcH);

  return out.toDataURL('image/webp');
}

export type MediaDisplayProps = {
  // May be undefined briefly for example with unified sources while adding a timeline event source
  // need to investigate if there's a better way to make sure we always have this before accessing it
  mediaItem: MediaSource | undefined,
  containerHeight?: number,
  containerWidth?: number,
  isPresentation?: boolean,
  isAccordion?: boolean,
  isHorizontalTimeline?: boolean,
  isTable?: boolean,
  isEventModal?: boolean,
  showTranscript?: boolean,
  showTranscriptMarkers?: boolean,
  transcriptPosition?: 'below' | 'side',
  updateMediaContext?: (field: string | object, value?: any) => void,
  /**
   * Whether `updateMediaContext` will actually accept writes. Callers that pass
   * `updateMediaContext` unconditionally (it is also their read path) set this so the
   * capture apply/remove controls render disabled rather than letting a click look
   * like it worked while the write is dropped.
   */
  editingEnabled?: boolean,
  isTitleSlide?: boolean,
  isUnifiedSources?: boolean,
  isPreview?: boolean,
  isLinkedMedia?: boolean,
  sourcesExpanded?: boolean,
  /** Flex fill slot (Favorites drawer, evidence locker overlay): bounded spreadsheet + drawer image layout. */
  fillContainer?: boolean,
  showToolbarExpandButton?: boolean,
  isSourceCard?: boolean,
  /** Included on /meta-viewer links from MetaFileDisplay when set. */
  caseId?: string | null,
  /** When true, MetaFileDisplay does not render the SMV button (e.g. source card renders it above citation). */
  hideMetaSocialViewerButton?: boolean,
  /** Mailbox attachment pane: hide Full Media / Capture and the capture actions. */
  hideCaptureControls?: boolean,
  /** When set, shows Obfuscated/Original segment control in the media toolbar (right side). */
  showObfuscatedPreview?: boolean,
  onToggleObfuscatedPreview?: (show: boolean) => void,
  /** Content-moderation detection type (e.g. ``'CSAM'``) when this media is flagged; captures of it derive an obfuscated variant. */
  contentModerationType?: string,
  /** App-level fullscreen viewer in ``main.tsx``; only this instance renders the portal. */
  isAppFullscreenViewer?: boolean,
  /** Citation-selection identity (source card). Re-arms markdown citation autoscroll on each click, even for identical text/file. */
  citationScrollKey?: string,
  /**
   * Stable per-chip identity when multiple media rows share one file (event modal).
   * Changes on chip switch but not during in-place trim edits on the same chip.
   */
  mediaSelectionKey?: string,
  /**
   * Width "credit" for chrome the caller renders alongside MediaDisplay in the same
   * row (e.g. presentation MediaPane's prev/next carets) — see MediaPlayerProps'
   * narrowLayoutWidthAllowancePx for why this only affects the side/below collapse
   * decision, not actual video sizing.
   */
  narrowLayoutWidthAllowancePx?: number,
  /** When true, suppresses media toolbar, PDF page nav, and player controls (Courtroom mode). */
  hideMediaChrome?: boolean,
  /**
   * Horizontal-timeline pop-up only. Closing the maximized view saves the
   * Full Media | Capture switcher choice onto the event. Other hosts keep the
   * view-only handoff (TR-3019).
   */
  commitViewChoiceOnClose?: boolean,
  /**
   * Event or slide that owns this viewer. Paging stays on that event when the
   * same PDF is cited elsewhere.
   */
  viewingPageScope?: string,
  /**
   * When set, the document viewer shows this page. Horizontal-timeline cards
   * pass the shared card page so the event modal and the card stay together.
   */
  viewingPage?: number,
  /** Fires when the PDF page changes, including the page restored on mount. */
  onViewingPageChange?: (page: number) => void,
  /**
   * Maximized Presentation confirms the crop in the Capture Region dialog.
   * Other fullscreen viewers save on mouse-up.
   */
  confirmCaptureRegion?: boolean,
}

export interface MediaDisplayRef {
  toggleFullScreen: () => void;
}

const MediaDisplayComponent = forwardRef<MediaDisplayRef, MediaDisplayProps>(({
  mediaItem,
  containerHeight,
  containerWidth,
  isPresentation,
  isAccordion,
  isHorizontalTimeline,
  isTable,
  isEventModal,
  showTranscript,
  showTranscriptMarkers,
  transcriptPosition = 'side',
  updateMediaContext,
  editingEnabled = true,
  isTitleSlide,
  isUnifiedSources,
  isPreview,
  isLinkedMedia,
  sourcesExpanded,
  fillContainer,
  showToolbarExpandButton = true,
  isSourceCard = false,
  caseId = null,
  hideMetaSocialViewerButton = false,
  hideCaptureControls = false,
  showObfuscatedPreview,
  onToggleObfuscatedPreview,
  contentModerationType,
  isAppFullscreenViewer = false,
  citationScrollKey,
  mediaSelectionKey,
  narrowLayoutWidthAllowancePx,
  hideMediaChrome = false,
  commitViewChoiceOnClose = false,
  viewingPageScope,
  viewingPage,
  onViewingPageChange,
  confirmCaptureRegion = false,
}: MediaDisplayProps, ref) => {
  const t = useTimePilotT();

  const textRef = useRef<HTMLDivElement>(null);
  const mediaDisplayRef = useRef<HTMLDivElement>(null);

  const [temporaryRotation, setTemporaryRotation] = useState<number | undefined>(undefined);
  const [imageErrors, setImageErrors] = useState<Record<string, { fileNotFound: boolean }>>({});
  const [htmlErrors, setHtmlErrors] = useState<Record<string, { fileNotFound: boolean }>>({});
  const [localZoomLevel, setLocalZoomLevel] = useState<number>(0);
  const [localContainerWidth, setLocalContainerWidth] = useState<number | undefined>(containerWidth);
  const [localContainerHeight, setLocalContainerHeight] = useState<number | undefined>(containerHeight);
  const [normalContainerDimensions, setNormalContainerDimensions] = useState<{width: number, height: number} | undefined>(undefined);
  const [itemToUse, setItemToUse] = useState<MediaSource | undefined>(undefined);
  const [viewAsCsvLike, setViewAsCsvLike] = useState<boolean>(false);
  const [canToggleCsv, setCanToggleCsv] = useState<boolean>(false);
  /** When a JSON citation also carries markdown-like ``content``, toggle which pane is shown. */
  const [jsonCitationViewMode, setJsonCitationViewMode] = useState<'markdown' | 'json'>('markdown');
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [normalContainerWidth, setNormalContainerWidth] = useState<number | undefined>(undefined);
  const [normalContainerHeight, setNormalContainerHeight] = useState<number | undefined>(undefined);
  /** Tracks which mbox email was fetched; */
  const mboxFetchedEmailIdRef = useRef<string | null>(null);

  const currentMboxFetchRef = useRef<{ emailId: string; abortController: AbortController } | null>(null);
  const currentPdfPageRef = useRef<number | null>(null);
  const currentPdfSearchRef = useRef<PdfSearchHandoffState | null>(null);
  // Capture selection is per citation instance, not per file: different
  // passages and later answers citing the same passage must not inherit one
  // another's crops (TR-3040, TR-3104).
  const initialCaptureIdentityKey = getMediaDisplayCaptureIdentityKey(mediaItem);
  const initialCaptureViewHandoff = getRememberedCaptureView(initialCaptureIdentityKey);
  const initialPersistedCaptureRef = resolvePersistedCaptureRef(mediaItem);
  const lastMediaItemKeyForViewCaptureRef = useRef<string | null>(
    initialCaptureViewHandoff !== undefined ? initialCaptureIdentityKey : null,
  );
  const wasFullscreenForCaptureViewRef = useRef(false);
  const maximizedCaptureViewKeyRef = useRef<string | null>(null);
  const lastMediaItemClipKeyRef = useRef<string>('');
  const lastMediaSelectionKeyRef = useRef<string>('');
  const checkSourceInfoRunRef = useRef(0);
  const transcriptEnrichmentRunRef = useRef(0);

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const captureFeatureEnabled = Boolean(useReactiveFeatureFlagValue('capture'));
  const { canSeeCsamTags, canSeeDescriptions, canRevealOriginal, canFlagMedia } = useCsamAccess();
  const powerUserOrg = Boolean(useReactiveFeatureFlagValue('powerUserOrg'));
  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();
  const themeWebsiteEnabled = useMediaPlayerTransitionEnabled('website');
  const themeHtmlEnabled = useMediaPlayerTransitionEnabled('html');
  const themeYoutubeEnabled = useMediaPlayerTransitionEnabled('youtube');
  const themeSpreadsheetEnabled = useMediaPlayerTransitionEnabled('spreadsheet');
  const { isFullscreen, setIsFullscreen, fullscreenMediaItem, setFullscreenMediaItem, linkedMediaItem, setLinkedMediaItem, setFullscreenObfuscation, fullscreenMediaEditing, setFullscreenMediaEditing } = useContext(FullScreenContext);
  const { zoomLevel: fullscreenZoomLevel } = useContext(FullscreenTransformStateContext);
  const { setZoomLevel: setFullscreenZoomLevel, setTemporaryRotation: setFullscreenTemporaryRotation } = useContext(FullscreenTransformActionsContext);
  const { setToast, setToastOpen } = useContext(ToastContext);
  // Fullscreen zoom lives in FullscreenTransformProvider context; localZoomLevel is
  // SourceCard / inline only. Using local state in the portal made overflow stay
  // hidden and layout zoom appear to do nothing for video.
  const zoomLevel = isFullscreen ? fullscreenZoomLevel : localZoomLevel;
  const effectiveTemporaryRotation = temporaryRotation;
  const { cellId, accessToken } = useCellServiceSession();
  const overlayFileUrl = resolveFlagFileUrl(itemToUse) || resolveFlagFileUrl(mediaItem);
  const fileContentModeration =
    itemToUse?.fileInfo?.contentModeration ?? mediaItem?.fileInfo?.contentModeration ?? null;
  const manualObfuscationJobStatus = useManualObfuscationJobStatus(
    overlayFileUrl,
    fileContentModeration,
  );
  const jobInProgress = manualObfuscationJobStatus === 'IN_PROGRESS';
  const itemPreviewUrl =
    itemToUse?.previewFilePath ||
    itemToUse?.fileInfo?.previewUrl ||
    mediaItem?.previewFilePath ||
    mediaItem?.fileInfo?.previewUrl ||
    '';
  const findingStatus = fileContentModeration?.findingStatus ?? null;
  const fileModerationType =
    fileContentModeration?.type && findingStatus !== 'REVIEWED'
      ? fileContentModeration.type
      : null;
  // Keyed to the file that was re-flagged. Switching to another cleared file
  // must not keep this latch, and a verifier clear during a blur must not set it.
  const [reflagUrl, setReflagUrl] = useState<string | null>(null);
  const baselineReviewed = isLocalObfuscationBaselineReviewed(overlayFileUrl);
  const pollObfuscation = usePollObfuscatedPreview(
    overlayFileUrl,
    Boolean(overlayFileUrl) &&
      manualObfuscationJobStatus !== 'FAILED' &&
      (baselineReviewed || !itemPreviewUrl) &&
      (jobInProgress ||
        (findingStatus !== 'REVIEWED' &&
          (Boolean(contentModerationType) || Boolean(fileModerationType)))),
    {
      generation: obfuscationJobGeneration(overlayFileUrl),
      awaitingReflag: baselineReviewed,
    },
  );
  useEffect(() => {
    if (!overlayFileUrl) return;
    if (pollObfuscation.cleared) {
      setReflagUrl((current) => (current === overlayFileUrl ? null : current));
      return;
    }
    if (baselineReviewed) {
      setReflagUrl(overlayFileUrl);
      return;
    }
    if (findingStatus !== 'REVIEWED') {
      setReflagUrl((current) => (current === overlayFileUrl ? null : current));
    }
  }, [overlayFileUrl, baselineReviewed, findingStatus, pollObfuscation.cleared]);
  const obfuscationOverridesCleared =
    findingStatus === 'REVIEWED' &&
    !pollObfuscation.cleared &&
    (baselineReviewed || reflagUrl === overlayFileUrl);
  const boundSafePreview = resolveBoundSafePreview({
    itemPreviewUrl,
    pollPreviewUrl: pollObfuscation.previewUrl,
    ignoreStalePreview: obfuscationOverridesCleared,
  });
  const hasSafePreview = Boolean(boundSafePreview);
  const effectiveContentModerationType = resolveEffectiveContentModerationType({
    contentModerationType,
    fileModerationType,
    findingStatus,
    pollPreviewUrl: pollObfuscation.previewUrl,
    obfuscationInProgress: obfuscationOverridesCleared,
  });
  const obfuscationFailed =
    pollObfuscation.failed || manualObfuscationJobStatus === 'FAILED';
  const obfuscationProcessing =
    !obfuscationFailed &&
    (jobInProgress ||
      pollObfuscation.timedOut ||
      pollObfuscation.isPolling ||
      (Boolean(effectiveContentModerationType) && !hasSafePreview));
  const obfuscationState: MediaObfuscationState = obfuscationFailed
    ? 'failed'
    : pollObfuscation.timedOut
      ? 'slow'
      : 'pending';
  const sharedCaseData = useOptionalToolsSharedCaseData();
  const captureApplyNonce = useToolsDrawerCaptureApplyNonce();
  const sharedCaseDataRef = useRef(sharedCaseData);
  sharedCaseDataRef.current = sharedCaseData;
  const pendingCaptureFavoriteRef = useRef<ToolsSharedCaptureEntry[]>([]);
  const itemToUseRef = useRef(itemToUse);
  itemToUseRef.current = itemToUse;
  const contentModerationTypeRef = useRef(contentModerationType);
  contentModerationTypeRef.current = contentModerationType;
  const canSeeCsamTagsRef = useRef(canSeeCsamTags);
  canSeeCsamTagsRef.current = canSeeCsamTags;
  const canSeeDescriptionsRef = useRef(canSeeDescriptions);
  canSeeDescriptionsRef.current = canSeeDescriptions;
  const showObfuscatedPreviewRef = useRef(showObfuscatedPreview);
  showObfuscatedPreviewRef.current = showObfuscatedPreview;
  const onToggleObfuscatedPreviewRef = useRef(onToggleObfuscatedPreview);
  onToggleObfuscatedPreviewRef.current = onToggleObfuscatedPreview;
  const updateMediaContextRef = useRef(updateMediaContext);
  updateMediaContextRef.current = updateMediaContext;
  const fullscreenMediaEditingRef = useRef(fullscreenMediaEditing);
  fullscreenMediaEditingRef.current = fullscreenMediaEditing;
  const fullscreenMediaItemRef = useRef(fullscreenMediaItem);
  fullscreenMediaItemRef.current = fullscreenMediaItem;
  const commitViewChoiceOnCloseRef = useRef(commitViewChoiceOnClose);
  commitViewChoiceOnCloseRef.current = commitViewChoiceOnClose;
  const persistDisplayedMediaChoiceRef = useRef<
    ((useCapture: boolean) => Promise<boolean>) | null
  >(null);
  const stagedCaptureForCommitRef = useRef('');

  /**
   * Persist trim / still-frame / layout edits from a fullscreen session back to the owner.
   * Layout keys are included only when they differ from the fullscreen-entry baseline so
   * remount continuity stamps (e.g. transcriptPosition) do not dirty EventModal on a
   * bare maximize/minimize, while real toggles still flush.
   */
  const flushMediaContextEdits = useCallback((source: MediaSource | null | undefined) => {
    const ctx = source?.mediaContext as Record<string, unknown> | undefined;
    if (!ctx) return;
    const updater =
      updateMediaContextRef.current ??
      (isAppFullscreenViewer ? fullscreenMediaEditingRef.current?.updateMediaContext : undefined);
    if (!updater) return;
    const payload: Record<string, unknown> = {};
    for (const key of FLUSH_ALWAYS_MEDIA_CONTEXT_KEYS) {
      if (ctx[key] !== undefined) {
        payload[key] = ctx[key];
      }
    }
    const layoutBaseline = fullscreenLayoutBaseline;
    for (const key of FLUSH_LAYOUT_MEDIA_CONTEXT_KEYS) {
      if (ctx[key] === undefined) continue;
      // Only flush layout fields the user changed after entry. No baseline means
      // another MediaDisplay already flushed and cleared the shared module state
      // (Escape exits owner + app viewer, and every instance shares isFullscreen) —
      // do not fall back to flushing present values or remount stamps (e.g.
      // transcriptPosition) will dirty EventModal after a bare maximize/Escape.
      if (!layoutBaseline || layoutBaseline[key] === ctx[key]) continue;
      payload[key] = ctx[key];
    }
    if (Object.keys(payload).length === 0) return;
    updater(payload);
  }, [isAppFullscreenViewer]);

  // Stable toggle registered into FullScreenContext for the app-level fullscreen viewer. It
  // updates the switcher's selection synchronously (so the segment control always reflects the
  // click, regardless of whether the owning source card re-renders in time) and delegates to the
  // owning source card's parent handler, which rebuilds the inline source and lets the matching
  // effect swap the displayed image variant.
  const wrappedObfuscationToggle = useCallback((show: boolean) => {
    onToggleObfuscatedPreviewRef.current?.(show);
    setFullscreenObfuscation((prev) =>
      prev && prev.showObfuscatedPreview === show ? prev : prev ? { ...prev, showObfuscatedPreview: show } : prev,
    );
  }, [setFullscreenObfuscation]);

  const metaViewerCaseId = useMemo(() => {
    const fromProp =
      caseId != null && String(caseId).trim() !== "" ? String(caseId).trim() : undefined;
    const fromShared =
      sharedCaseData?.caseId != null && String(sharedCaseData.caseId).trim() !== ""
        ? String(sharedCaseData.caseId).trim()
        : undefined;
    return fromProp ?? fromShared ?? null;
  }, [caseId, sharedCaseData?.caseId]);

  /** Fullscreen overlay covers the entire viewport. `body.media-fullscreen`
   *  hides the primary nav (#main-navigation) and the work-product nav so
   *  the portaled overlay sits flush against the top edge — including when
   *  the fullscreen action is triggered from inside the event modal. */
  const fullscreenOverlayLayoutClass = 'top-0 h-screen';

  const prevCaptureApplyNonceRef = useRef<number | null>(null);

  const propsClipSourceItem = getMediaDisplayClipSourceItem(
    mediaItem,
    isFullscreen,
    fullscreenMediaItem,
    isLinkedMedia,
  );
  /**
   * Which media is loaded (file plus cited page). Gates whether ``itemToUse``
   * may paint, so it has to stay in the same key space as
   * ``lastMediaItemClipKeyRef``. Deliberately not citation-instance scoped: two
   * answers citing one passage load the very same media.
   */
  const propsClipKey = getMediaDisplayClipIdentityKey(propsClipSourceItem);
  /**
   * Citation instance this viewer is showing, derived from props so the
   * capture staging write and the read that decides whether to paint it cannot
   * disagree. Mirrored into a ref for ``handleUpdateCapture``, which runs
   * outside render (TR-3040, TR-3104).
   */
  const propsCitationKey = getMediaDisplayCaptureIdentityKey(propsClipSourceItem);
  const propsCitationKeyRef = useRef(propsCitationKey);
  propsCitationKeyRef.current = propsCitationKey;

  const ownsFullscreenPortal =
    Boolean(isLinkedMedia && linkedMediaItem) ||
    Boolean(isAppFullscreenViewer && isFullscreen && fullscreenMediaItem);

  const isFullscreenOverlay =
    ownsFullscreenPortal &&
    !isTable &&
    !isAccordion &&
    !isUnifiedSources;

  useEffect(() => {
    if (!isFullscreenOverlay) {
      document.body.classList.remove('media-fullscreen');
      return;
    }
    document.body.classList.add('media-fullscreen');
    return () => document.body.classList.remove('media-fullscreen');
  }, [isFullscreenOverlay]);

  const captureRef = useRef<CaptureRegion | undefined>(undefined);
  const captureRotationRef = useRef(0);
  captureRotationRef.current =
    ((((effectiveTemporaryRotation ?? itemToUse?.mediaContext?.rotation ?? 0) % 360) + 360) % 360);
  const captureOverlayRef = useRef<CaptureRegion | undefined>(undefined);
  const captureDraggingRef = useRef(false);
  const [captureDragActive, setCaptureDragActive] = useState(false);
  const capModalRef = useRef<HTMLDivElement>(null);
  const captureResultCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const autoSaveCaptureOnCommitRef = useRef(false);
  const confirmCaptureRegionRef = useRef(confirmCaptureRegion);
  confirmCaptureRegionRef.current = confirmCaptureRegion;
  const capturePersistEpochRef = useRef(0);
  const [captureMode, setCaptureMode] = useState<boolean>(false);
  const captureModeRef = useRef(false);
  captureModeRef.current = captureMode;
  const [captureRegion, setCaptureRegion] = useState<CaptureRegion | undefined>(undefined);
  const [captureRegionOverlay, setCaptureRegionOverlay] = useState<CaptureRegion | undefined>(undefined);
  const [captureCursor, setCaptureCursor] = useState<{ x: number; y: number } | null>(null);
  const [capturePreviewSrc, setCapturePreviewSrc] = useState<string | undefined>(
    () => initialCaptureViewHandoff?.previewSrc,
  );
  const [capturePreviewPending, setCapturePreviewPending] = useState(false);
  const [capturePreviewError, setCapturePreviewError] = useState<string | undefined>(undefined);
  const [captureSaveError, setCaptureSaveError] = useState<string | undefined>(undefined);
  const [capturedDataAvailable, setCapturedDataAvailable] = useState<CapturedDataObject | undefined>(undefined);
  const [applyingCapturedData, setApplyingCapturedData] = useState<boolean>(false);
  const [removingCapturedData, setRemovingCapturedData] = useState<boolean>(false);
  const [viewCapture, setViewCapture] = useState<boolean>(
    () =>
      resolveInitialViewCapture({
        remembered: initialCaptureViewHandoff,
        hasPersistedCapture: Boolean(initialPersistedCaptureRef),
        // A Full Media choice recorded against a different capture (usually
        // none at all) must not hide the one now on the media (TR-3019).
        currentCaptureRef: initialPersistedCaptureRef,
        // Inline MediaPane only. The app-level maximize viewer also receives
        // isPresentation for chat/courtroom/horizontal layout and must adopt
        // the remembered Full Media | Capture side instead of forcing Capture.
        preferCapture: Boolean(isPresentation) && !isAppFullscreenViewer,
      }),
  );
  const viewCaptureRef = useRef(viewCapture);
  viewCaptureRef.current = viewCapture;
  const capturePreviewSrcRef = useRef(capturePreviewSrc);
  capturePreviewSrcRef.current = capturePreviewSrc;
  /**
   * Set the Full Media / Capture selection and stamp the ref in the same tick.
   *
   * ``viewCaptureRef`` is otherwise assigned during render, so a handler that
   * selects Capture and closes fullscreen in one batch leaves
   * ``toggleFullScreen`` reading the pre-click value — handing the regular
   * viewer the stale selection it then restores (TR-3022).
   *
   * This is the explicit-decision path — Use Full Media, Use Capture, and the
   * switch to Capture after a save — so it records which capture the choice was
   * about. The mirroring writers deliberately cannot, since they also run for
   * viewers replaying a selection made before the current crop existed
   * (TR-3019).
   */
  const commitViewCapture = useCallback((next: boolean) => {
    viewCaptureRef.current = next;
    setViewCapture(next);
    const decidedAbout = itemToUseRef.current ?? mediaItem;
    rememberCaptureView(
      getMediaDisplayCaptureIdentityKey(decidedAbout),
      next,
      capturePreviewSrcRef.current,
      resolvePersistedCaptureRef(decidedAbout),
    );
  }, [mediaItem]);
  const [pdfPageReady, setPdfPageReady] = useState<boolean>(false);

  useEffect(() => {
    // While the maximize view is up it owns the selection, and this instance's
    // state is frozen at whatever it was before the handoff. Writing that back
    // would clobber the choice made in maximize — including on the exit commit,
    // where this effect runs before the adopt effect below and would otherwise
    // hand it the pre-maximize value to restore (TR-3022).
    if (!isAppFullscreenViewer && (isFullscreen || wasFullscreenForCaptureViewRef.current)) {
      return;
    }
    const rememberedItem = itemToUse ?? mediaItem;
    const key = getMediaDisplayCaptureIdentityKey(rememberedItem);
    if (!key || lastMediaItemKeyForViewCaptureRef.current !== key) return;
    mirrorCaptureViewState(
      key,
      viewCapture,
      capturePreviewSrc,
      resolvePersistedCaptureRef(rememberedItem),
    );
  }, [
    viewCapture,
    capturePreviewSrc,
    itemToUse,
    mediaItem,
    isFullscreen,
    isAppFullscreenViewer,
  ]);

  /**
   * Adopt the selection made in the maximize view once it closes.
   *
   * The owner renders ``null`` while the app-level viewer is up rather than
   * unmounting, so the handoff read in the ``viewCapture`` initializer never
   * runs again and ``checkSourceInfo`` only re-initializes on a media identity
   * change. Without this, Capture chosen in maximize — by the switcher or by
   * Use Capture — was written to the handoff and then ignored on the way back,
   * leaving the regular viewer on Full Media (TR-3022).
   */
  useEffect(() => {
    const wasFullscreen = wasFullscreenForCaptureViewRef.current;
    wasFullscreenForCaptureViewRef.current = isFullscreen;
    if (isFullscreen) {
      // Recorded while the maximize view is up because the exit commit can
      // already have cleared fullscreenMediaItem.
      const maximizedKey = getMediaDisplayCaptureIdentityKey(fullscreenMediaItem ?? undefined);
      if (maximizedKey) maximizedCaptureViewKeyRef.current = maximizedKey;
    }
    const key = getMediaDisplayCaptureIdentityKey(itemToUseRef.current ?? mediaItem);
    const adopted = resolveCaptureViewOnFullscreenExit({
      isAppFullscreenViewer: Boolean(isAppFullscreenViewer),
      isFullscreen,
      wasFullscreen,
      showsMaximizedMedia: Boolean(key) && key === maximizedCaptureViewKeyRef.current,
      remembered: getRememberedCaptureView(key),
    });
    if (!adopted) return;
    lastMediaItemKeyForViewCaptureRef.current = key;
    setViewCapture(adopted.viewCapture);
    if (adopted.previewSrc) {
      setCapturePreviewSrc(adopted.previewSrc);
    }
  }, [isFullscreen, fullscreenMediaItem, isAppFullscreenViewer, mediaItem]);

  useEffect(() => {
    if (!isEventModal) {
      prevCaptureApplyNonceRef.current = null;
      return;
    }
    if (prevCaptureApplyNonceRef.current === null) {
      prevCaptureApplyNonceRef.current = captureApplyNonce;
      return;
    }
    if (captureApplyNonce !== prevCaptureApplyNonceRef.current) {
      prevCaptureApplyNonceRef.current = captureApplyNonce;
      setViewCapture(true);
    }
  }, [isEventModal, captureApplyNonce]);

  useEffect(() => {
    // Default to markdown excerpt when the citation *content identity* changes.
    // Do not depend on sourceFilePath / URLs — those often fill in after async
    // resolution and would yank the user back from full JSON to markdown.
    setJsonCitationViewMode('markdown');
  }, [mediaItem?.mediaType, mediaItem?.content]);

  useEffect(() => {
    if (!mediaItem) return;
    // `undefined` means the parent has not loaded a capture yet — do not wipe a
    // locally applied still. An explicit `''` from Use Full Media must clear.
    if (!isExplicitlyClearedCapture(mediaItem) && !resolvePersistedCaptureRef(mediaItem)) {
      return;
    }
    const capture = resolvePersistedCaptureRef(mediaItem);
    if (
      isExplicitlyClearedCapture(mediaItem) &&
      resolvePersistedCaptureRef(itemToUseRef.current)
    ) {
      setViewCapture(false);
    }
    setItemToUse((prev) => {
      if (prev) {
        const prevCapture = resolvePersistedCaptureRef(prev);
        if (prevCapture === capture) return prev;
        const isAv = prev.mediaType === 'video' || prev.mediaType === 'audio';
        const nextContext = {
          ...prev.mediaContext,
          capture: capture || '',
        } as NonNullable<MediaSource['mediaContext']>;
        if (!capture && isAv) {
          nextContext.stillFrame = '';
          nextContext.canvas = {
            ...(prev.mediaContext?.canvas ?? {}),
            stillFrameImage: '',
            stillFrame: '',
          };
        }
        return {
          ...prev,
          mediaContext: nextContext,
        } as MediaSource;
      }
      if (!capture) return prev;
      // Displayed Media tiles remount when the fullscreen editor closes. Seed
      // itemToUse from props so the thumbnail still paints before checkSourceInfo
      // resolves srcToUse for PDF / document viewers.
      return {
        ...mediaItem,
        srcToUse: mediaItem.srcToUse ?? getSrcToUse(mediaItem),
      };
    });
  }, [mediaItem?.mediaContext?.capture, mediaItem]);

  /**
   * Apply a saved capture to the open item, stamping the ref in the same tick
   * as the state.
   *
   * Use Capture exits fullscreen as soon as the save resolves, and that exit
   * flushes mediaContext out of ``itemToUseRef`` — which React has not
   * re-rendered yet. Without the synchronous stamp the flush writes the
   * pre-save ``capture: ''`` back over the still that was just uploaded and the
   * tile falls back to "Add Preview".
   */
  const stampItemToUseWithCapturePatch = useCallback(
    (capturePatch: Record<string, unknown>) => {
      const applyPatch = (media: MediaSource): MediaSource => ({
        ...media,
        mediaContext: {
          ...media.mediaContext,
          ...capturePatch,
        } as MediaSource['mediaContext'],
      });
      const current = itemToUseRef.current;
      if (current) {
        itemToUseRef.current = applyPatch(current);
      }
      setItemToUse((prev) => (prev ? applyPatch(prev) : prev));
    },
    [],
  );

  /**
   * Queue an uploaded still into Favorites. Shared by the regular and
   * thumbnail-edit save paths so a moderated capture cannot reach Favorites
   * without its obfuscated preview.
   */
  const queueCaptureFavorite = useCallback(
    (args: {
      s3Uri: string;
      cmType?: string;
      obfuscatedCaptureData?: string;
      imageDescription?: string;
      log: CaptureSaveLog;
    }): void => {
      const entry = buildCaptureFavoriteEntry({
        s3Uri: args.s3Uri,
        contentModerationType: args.cmType,
        obfuscatedCaptureData: args.obfuscatedCaptureData,
        imageDescription: args.imageDescription,
      });
      // Read the live sink after the await. A snapshot taken before upload can
      // still look unhydrated after the bundle has already hydrated, which would
      // queue into a ref the hydration effect already ran against (TR-2874).
      const liveRemote = sharedCaseDataRef.current;
      pendingCaptureFavoriteRef.current = persistCaptureFavorite(
        liveRemote,
        entry,
        pendingCaptureFavoriteRef.current,
      );
      // The favorites row is written by a debounced request that reports failure
      // only to the console, so record what was handed off. When the capture is
      // missing after a reload but this line is present, the write is the suspect.
      args.log.step('favorites-queue', {
        entryId: entry.id,
        moderated: Boolean(entry.contentModerationType),
        obfuscationCarried: Boolean(entry.obfuscatedWebpBase64),
        sinkHydrated: Boolean(liveRemote?.hydrated),
        stillQueued: pendingCaptureFavoriteRef.current.length,
      });
    },
    [],
  );

  const handleUpdateCapture = useCallback(async (
    captureData: string | undefined,
    obfuscatedCaptureData?: string,
    cmType?: string,
    imageDescription?: string,
  ): Promise<boolean> => {
    if (!captureData) {
      const emptyObject: CapturedDataObject = {
        s3Uri: '',
        sourceKey: '',
      };
      localStorage.setItem('captureData', JSON.stringify(emptyObject));
      setCapturedDataAvailable(emptyObject);
      return true;
    }
    const latestItem = itemToUseRef.current;
    const { sourceKey, sourceMd5 } = originBindingFromMedia(latestItem);
    const remote = sharedCaseDataRef.current;
    const selectedCaseId =
      metaViewerCaseId ??
      (remote?.caseId != null ? String(remote.caseId).trim() : undefined);
    const caseIdForUpload = resolveUserImageUploadCaseId({
      selectedCaseId,
      media: latestItem,
      sourceKey,
    });

    const isCsamCapture = Boolean(
      canSeeCsamTagsRef.current && contentModerationTypeRef.current,
    );
    const log = startCaptureSave({
      purpose: isCsamCapture ? 'csam-capture' : 'capture-favorite',
      caseId: caseIdForUpload ?? null,
      selectedCaseId: selectedCaseId ?? null,
      mediaType: latestItem?.mediaType ?? null,
      hasSourceKey: Boolean(sourceKey?.trim()),
      hasSourceMd5: Boolean(sourceMd5),
    });

    if (!cellId || !accessToken || !caseIdForUpload) {
      log.skipped('resolve-case', 'no session or no resolvable case for this media', {
        hasCellId: Boolean(cellId),
        hasAccessToken: Boolean(accessToken),
        caseIdForUpload: caseIdForUpload ?? null,
        selectedCaseId: selectedCaseId ?? null,
      });
      return false;
    }

    if (isCsamCapture && !sourceKey?.trim()) {
      log.skipped(
        'resolve-case',
        'CSAM capture requires a parent sourceKey for tag inheritance',
      );
      return false;
    }

    const persistEpoch = capturePersistEpochRef.current;

    let s3Uri: string;
    try {
      ({ s3Uri } = await uploadUserImage({
        imageData: captureData,
        mimeType: 'image/webp',
        purpose: isCsamCapture ? 'csam-capture' : 'capture-favorite',
        caseId: caseIdForUpload,
        tenantFriendlyName: remote?.tenantFriendlyName ?? undefined,
        sourceMd5,
        sourceKey: sourceKey || undefined,
        cellId,
        accessToken,
        log,
      }));
    } catch (err) {
      if (isCaptureSaveLogEnabled()) {
        console.error(`[capture-save] ${log.attempt} upload threw`, err);
      } else {
        console.error('Failed to upload capture favorite', err);
      }
      return false;
    }

    // The upload succeeded but the user has since cancelled, redrawn, or moved
    // to other media, so the result is dropped on purpose — logged because it
    // otherwise looks identical to a save that silently did nothing.
    if (capturePersistEpochRef.current !== persistEpoch) {
      log.skipped('attach-to-media', 'superseded before the upload finished', { s3Uri });
      return false;
    }

    // Stamp the exact answer/source citation instance: the reader gates on
    // this so the crop cannot paint on another passage or a later answer.
    const captureCitationKey =
      propsCitationKeyRef.current || getMediaDisplayCaptureIdentityKey(latestItem ?? mediaItem);
    const captureDataObject: CapturedDataObject = {
      s3Uri,
      sourceKey,
      ...(caseIdForUpload ? { caseId: caseIdForUpload } : {}),
      ...(captureCitationKey ? { citationKey: captureCitationKey } : {}),
    };
    localStorage.setItem('captureData', JSON.stringify(captureDataObject));
    setCapturedDataAvailable(captureDataObject);

    queueCaptureFavorite({
      s3Uri,
      cmType,
      obfuscatedCaptureData,
      imageDescription,
      log,
    });

    // Attach the still to the open item and switch to Thumbnail immediately so
    // Full Media | Thumbnail is not stuck on the original while parent
    // mediaContext catches up, and so compact tiles still paint after fullscreen
    // remounts (TR-2920).
    const item = itemToUseRef.current;
    const isAv = item?.mediaType === 'video' || item?.mediaType === 'audio';
    const updater =
      updateMediaContextRef.current ??
      fullscreenMediaEditingRef.current?.updateMediaContext;
    const capturePatch: Record<string, unknown> = {
      capture: s3Uri,
      lastUpdated: Date.now(),
    };
    if (isAv) {
      const video = mediaDisplayRef.current?.querySelector('video');
      const stillLabel =
        video != null
          ? video.currentTime.toFixed(0)
          : item?.mediaContext?.stillFrame;
      capturePatch.stillFrame = stillLabel;
      capturePatch.canvas = {
        ...((item?.mediaContext?.canvas as Record<string, unknown> | undefined) ?? {}),
        stillFrameImage: s3Uri,
        stillFrame: stillLabel,
      };
    }
    if (!updater) {
      log.skipped(
        'attach-to-media',
        'no media context updater registered; the tile will not show the capture',
      );
    }
    updater?.(capturePatch);
    stampItemToUseWithCapturePatch(capturePatch);
    setViewCapture(true);
    log.done({ s3Uri });
    return true;
  }, [
    metaViewerCaseId,
    cellId,
    accessToken,
    stampItemToUseWithCapturePatch,
    queueCaptureFavorite,
  ]);

  useEffect(() => {
    const snapshot = pendingCaptureFavoriteRef.current;
    const remaining = flushQueuedCaptureFavorites(sharedCaseData, snapshot);
    if (pendingCaptureFavoriteRef.current === snapshot) {
      pendingCaptureFavoriteRef.current = remaining;
      return;
    }
    const extras = pendingCaptureFavoriteRef.current.filter(
      (queued) => !snapshot.some((prior) => prior.id === queued.id),
    );
    pendingCaptureFavoriteRef.current = [...remaining, ...extras];
  }, [sharedCaseData, sharedCaseData?.hydrated]);

  const handleCaptureRegion = useCallback(
    (cap: CaptureRegion | undefined): Promise<boolean> => {
      const captureCanvas =
        captureResultCanvasRef.current ??
        (document.getElementById('imageCaptureCanvas') as HTMLCanvasElement | null);
      if (
        captureCanvas &&
        cap &&
        cap.width !== undefined &&
        cap.height !== undefined &&
        Math.abs(cap.width) > 0 &&
        Math.abs(cap.height) > 0 &&
        cap.x !== undefined &&
        cap.y !== undefined
      ) {
        const persistEpoch = capturePersistEpochRef.current;
        const captureData = captureCanvas.toDataURL('image/webp', CAPTURE_WEBP_QUALITY);
        const notifyClipboard = () => {
          const blockClipboard = shouldBlockCaptureClipboardForOriginalCsam({
            canSeeCsamTags: canSeeCsamTagsRef.current,
            contentModerationType: contentModerationTypeRef.current,
            showObfuscatedPreview: showObfuscatedPreviewRef.current,
            bypassCm: itemToUseRef.current?.bypassCm,
          });
          if (blockClipboard) {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: 3000,
              title: t('media.captureSaved'),
              description: t('media.captureClipboardBlocked'),
              level: ToastLevel.SUCCESS,
            });
            return;
          }
          void copyCaptureCanvasToClipboard(captureCanvas).then((copied) => {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: 3000,
              title: t('media.captureSaved'),
              description: copied
                ? t('media.captureCopied')
                : t('media.captureClipboardUnavailable'),
              level: copied ? ToastLevel.SUCCESS : ToastLevel.WARNING,
            });
          });
        };
        const notifySaveFailed = () => {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 4000,
            title: t('media.captureNotSaved'),
            description: t('media.captureUploadFailed'),
            level: ToastLevel.ERROR,
          });
        };
        const playingVideo = mediaDisplayRef.current?.querySelector('video');
        // Derived before the thumbnail-edit branch: a moderated still must reach
        // Favorites with its obfuscated preview whichever path saves it.
        const cmType = canSeeCsamTagsRef.current
          ? contentModerationTypeRef.current
          : undefined;
        const obfuscatedData = cmType ? deriveObfuscatedCaptureDataUrl(captureCanvas) : undefined;
        const imageDescription =
          cmType && canSeeDescriptionsRef.current
            ? itemToUseRef.current?.fileInfo?.contentModeration?.imageDescription ?? undefined
            : undefined;
        const thumbnailEdit = Boolean(
          fullscreenMediaEditingRef.current?.thumbnailEditMode,
        );

        if (thumbnailEdit) {
          const latestItem = itemToUseRef.current;
          const isAv =
            latestItem?.mediaType === 'video' || latestItem?.mediaType === 'audio';
          const updater =
            updateMediaContextRef.current ??
            fullscreenMediaEditingRef.current?.updateMediaContext;
          const remoteForThumbnail = sharedCaseDataRef.current;
          const selectedCaseIdForThumbnail =
            metaViewerCaseId ??
            (remoteForThumbnail?.caseId != null
              ? String(remoteForThumbnail.caseId).trim()
              : undefined);
          const thumbnailBinding = originBindingFromMedia(latestItem);
          const caseIdForThumbnail = resolveUserImageUploadCaseId({
            selectedCaseId: selectedCaseIdForThumbnail,
            media: latestItem,
            sourceKey: thumbnailBinding.sourceKey,
          });
          const thumbnailPurpose = isAv
            ? 'video-still-frame'
            : latestItem?.mediaType === 'pdf' ||
                latestItem?.mediaType === 'docx' ||
                latestItem?.mediaType === 'pptx'
              ? 'pdf-page-thumbnail'
              : 'event-media-capture';
          const log = startCaptureSave({
            purpose: thumbnailPurpose,
            thumbnailEdit: true,
            caseId: caseIdForThumbnail ?? null,
            selectedCaseId: selectedCaseIdForThumbnail ?? null,
            mediaType: latestItem?.mediaType ?? null,
            hasSourceKey: Boolean(thumbnailBinding.sourceKey?.trim()),
            hasSourceMd5: Boolean(thumbnailBinding.sourceMd5),
          });

          const persistThumbnailEditCapture = async () => {
            if (!cellId || !accessToken || !caseIdForThumbnail) {
              log.skipped('resolve-case', 'no session or no resolvable case for this media', {
                hasCellId: Boolean(cellId),
                hasAccessToken: Boolean(accessToken),
                caseIdForUpload: caseIdForThumbnail ?? null,
                selectedCaseId: selectedCaseIdForThumbnail ?? null,
              });
              return '';
            }
            try {
              const { s3Uri } = await uploadUserImage({
                imageData: captureData,
                mimeType: 'image/webp',
                purpose: thumbnailPurpose,
                caseId: caseIdForThumbnail,
                tenantFriendlyName: remoteForThumbnail?.tenantFriendlyName ?? undefined,
                sourceKey: thumbnailBinding.sourceKey,
                sourceMd5: thumbnailBinding.sourceMd5,
                cellId,
                accessToken,
                log,
              });
              return persistableCaptureRef(s3Uri);
            } catch (err) {
              if (isCaptureSaveLogEnabled()) {
                console.error(`[capture-save] ${log.attempt} upload threw`, err);
              } else {
                console.warn(
                  'Failed to upload thumbnail still:',
                  err instanceof Error ? err.message : err,
                );
              }
              return '';
            }
          };

          const persist = (async () => {
            const imageRef = persistableCaptureRef(await persistThumbnailEditCapture());
            if (capturePersistEpochRef.current !== persistEpoch) {
              log.skipped('attach-to-media', 'superseded before the upload finished', {
                imageRef: imageRef || null,
              });
              return false;
            }
            if (!imageRef) {
              notifySaveFailed();
              setCaptureSaveError('Could not save this capture. Try again.');
              return false;
            }
            queueCaptureFavorite({
              s3Uri: imageRef,
              cmType,
              obfuscatedCaptureData: obfuscatedData,
              imageDescription,
              log,
            });
            const capturePatch: Record<string, unknown> = { capture: imageRef };
            if (isAv) {
              const stillFrameLabel = (playingVideo?.currentTime ?? 0).toFixed(0);
              capturePatch.stillFrame = stillFrameLabel;
              capturePatch.canvas = {
                ...latestItem?.mediaContext?.canvas,
                stillFrameImage: imageRef,
                stillFrame: stillFrameLabel,
              };
            } else if (
              latestItem?.mediaType === 'pdf' ||
              latestItem?.mediaType === 'docx' ||
              latestItem?.mediaType === 'pptx'
            ) {
              const pageNumber = parseCitationPageFromMedia(latestItem);
              capturePatch.pageNumber = pageNumber;
              capturePatch.canvas = {
                ...latestItem?.mediaContext?.canvas,
                image: imageRef,
                pageNumber,
              };
            }
            if (!updater) {
              log.skipped(
                'attach-to-media',
                'no media context updater registered; the Displayed Media tile will not show it',
              );
            }
            updater?.(capturePatch);
            // Mirror the patch locally so this viewer (and the compact tile it
            // remounts into) paints the still without waiting for a parent refetch.
            stampItemToUseWithCapturePatch(capturePatch);
            notifyClipboard();
            setCaptureRegion(undefined);
            setCaptureRegionOverlay(undefined);
            setCapturePreviewSrc(undefined);
            log.done({ imageRef });
            return true;
          })();
          return persist;
        }

        const upload = handleUpdateCapture(captureData, obfuscatedData, cmType, imageDescription);
        return upload.then((saved) => {
          if (capturePersistEpochRef.current !== persistEpoch) return false;
          if (!saved) {
            notifySaveFailed();
            setCaptureSaveError('Could not save this capture. Try again.');
            return false;
          }
          notifyClipboard();
          setCaptureRegion(undefined);
          setCaptureRegionOverlay(undefined);
          setCapturePreviewSrc(undefined);
          return true;
        });
      }
      return Promise.resolve(false);
    },
    [
      handleUpdateCapture,
      metaViewerCaseId,
      cellId,
      accessToken,
      setToast,
      setToastOpen,
      stampItemToUseWithCapturePatch,
      queueCaptureFavorite,
    ],
  );

  const handleCaptureRegionRef = useRef(handleCaptureRegion);
  handleCaptureRegionRef.current = handleCaptureRegion;

  useEffect(() => {
    if (
      !captureRegion ||
      captureRegion.width === undefined ||
      captureRegion.height === undefined ||
      Math.abs(captureRegion.width) <= 0 ||
      Math.abs(captureRegion.height) <= 0 ||
      captureRegion.x === undefined ||
      captureRegion.y === undefined
    ) {
      captureResultCanvasRef.current = null;
      setCapturePreviewPending(false);
      setCapturePreviewError(undefined);
      return;
    }

    const mediaRoot = mediaDisplayRef.current;
    if (!mediaRoot) return;

    const normalizedCap = normalizedCaptureRegion(captureRegion);
    const playingVideo = mediaRoot.querySelector('video');
    const fallbackVideoSrc = resolveSameOriginCaptureSrcForVideo(
      playingVideo,
      itemToUseRef.current,
    );
    let cancelled = false;
    setCapturePreviewPending(true);
    setCapturePreviewSrc(undefined);
    setCapturePreviewError(undefined);
    captureResultCanvasRef.current = null;

    captureVideoRegionCanvas(mediaRoot, normalizedCap, fallbackVideoSrc, captureRotationRef.current)
      .then((videoCanvas) => {
        if (cancelled) return null;
        if (videoCanvas) {
          return { canvas: videoCanvas, isDirectVideoCanvas: true };
        }
        if (playingVideo) {
          throw new Error(
            'Could not create this capture. Try again after the video has finished loading.',
          );
        }
        // pdf.js page canvases cannot be cloned by html2canvas (tainted / 0×0
        // hiddenCanvasElement). Blit the visible page region directly.
        if (isPdfCaptureRoot(mediaRoot)) {
          return capturePdfRegionCanvasAsync(mediaRoot, normalizedCap).then((result) => {
            if (!result.canvas) {
              console.warn('PDF thumbnail capture failed:', result.reason ?? 'unknown');
              throw new Error(
                'Could not create this capture. Try selecting a different region.',
              );
            }
            return { canvas: result.canvas, isDirectVideoCanvas: true };
          });
        }
        return html2canvas(mediaRoot, {
          x: normalizedCap.x,
          y: normalizedCap.y,
          width: normalizedCap.width,
          height: normalizedCap.height,
          ignoreElements: (element) => {
            if (element.getAttribute('data-capture-overlay') === 'true') return true;
            if (element instanceof HTMLCanvasElement) return true;
            return false;
          },
        }).then((canvas) => ({ canvas, isDirectVideoCanvas: false }));
      })
      .then(async (result) => {
        if (cancelled || !result) return;
        const { canvas, isDirectVideoCanvas } = result;
        if (!isDirectVideoCanvas) {
          try {
            await drawVideoFrameOnCaptureCanvas(
              mediaRoot,
              canvas,
              normalizedCap,
              fallbackVideoSrc,
              captureRotationRef.current,
            );
          } catch (error) {
            console.warn(
              'Video frame capture failed:',
              error instanceof Error ? error.message : error,
            );
          }
          if (itemToUseRef.current?.mediaType === 'html') {
            try {
              await drawIframeDocumentOnCaptureCanvas(mediaRoot, canvas, normalizedCap);
            } catch (error) {
              console.warn(
                'Iframe thumbnail capture failed:',
                error instanceof Error ? error.message : error,
              );
            }
          }
        }
        if (!canvasHasVisiblePixels(canvas)) {
          const message =
            'Could not create this capture. Try selecting a different region.';
          autoSaveCaptureOnCommitRef.current = false;
          captureResultCanvasRef.current = null;
          setCapturePreviewSrc(undefined);
          setCapturePreviewError(message);
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 3000,
            title: t('media.captureFailed'),
            description: t('media.captureFailedRegion'),
            level: ToastLevel.WARNING,
          });
          return;
        }
        canvas.id = 'imageCaptureCanvas';
        captureResultCanvasRef.current = canvas;
        let previewSrc = '';
        try {
          previewSrc = canvas.toDataURL('image/webp', CAPTURE_WEBP_QUALITY);
          setCapturePreviewSrc(previewSrc);
        } catch (error) {
          console.warn(
            'Capture preview export failed:',
            error instanceof Error ? error.message : error,
          );
        }
        // Framing is done once the still exists: drop the hit layer, selection
        // mask and crosshair so the pane chrome and Use Thumbnail are reachable.
        // ``captureRegion`` stays so the commit still persists this crop.
        setCaptureMode(false);
        setCaptureRegionOverlay(undefined);
        setCaptureCursor(null);
        // Swap the pane to the crop on mouse-up so the user sees exactly what
        // Use Thumbnail will save. The switcher can flip back to Full Media.
        // Displayed Media thumbnail edit keeps the full media behind the Capture
        // Region modal until the user confirms (TR-3043).
        if (
          previewSrc &&
          isFullscreen &&
          !confirmCaptureRegionRef.current &&
          !(
            isAppFullscreenViewer &&
            fullscreenMediaEditingRef.current?.thumbnailEditMode
          )
        ) {
          setViewCapture(true);
        }
        if (autoSaveCaptureOnCommitRef.current) {
          autoSaveCaptureOnCommitRef.current = false;
          void handleCaptureRegionRef.current(normalizedCap).then((saved) => {
            if (!saved) return;
            setViewCapture(true);
            setCapturePreviewSrc(undefined);
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : t('media.captureFailedRetry');
        console.warn('media capture failed:', message);
        autoSaveCaptureOnCommitRef.current = false;
        captureResultCanvasRef.current = null;
        setCapturePreviewSrc(undefined);
        setCapturePreviewError(message);
      })
      .finally(() => {
        if (!cancelled) setCapturePreviewPending(false);
      });

    return () => {
      cancelled = true;
    };
  }, [captureRegion]);

  const beginCaptureDrag = useCallback((clientX: number, clientY: number, root: HTMLElement) => {
    capturePersistEpochRef.current += 1;
    const rect = root.getBoundingClientRect();
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    captureRef.current = { x: relX, y: relY, width: 0, height: 0 };
    captureOverlayRef.current = { x: relX, y: relY, width: 0, height: 0 };
    captureDraggingRef.current = true;
    setCaptureDragActive(true);
  }, []);

  const updateCaptureDrag = useCallback((clientX: number, clientY: number, root: HTMLElement) => {
    const rect = root.getBoundingClientRect();
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    const capOverlay = captureOverlayRef.current;
    if (
      !captureDraggingRef.current ||
      !capOverlay ||
      capOverlay.x === undefined ||
      capOverlay.y === undefined
    ) {
      return;
    }
    setCaptureRegionOverlay(
      normalizedCaptureRegion({
        x: capOverlay.x,
        y: capOverlay.y,
        width: relX - capOverlay.x,
        height: relY - capOverlay.y,
      }),
    );
  }, []);

  const commitCaptureDrag = useCallback(
    (clientX: number, clientY: number, root: HTMLElement) => {
      if (!captureDraggingRef.current) return;
      captureDraggingRef.current = false;
      setCaptureDragActive(false);
      const cap = captureRef.current;
      if (cap?.x === undefined || cap?.y === undefined) return;
      const rect = root.getBoundingClientRect();
      const nextRegion = {
        x: cap.x,
        y: cap.y,
        width: clientX - rect.left - cap.x,
        height: clientY - rect.top - cap.y,
      };
      captureRef.current = nextRegion;
      const normalized = normalizedCaptureRegion(nextRegion);
      captureOverlayRef.current = undefined;
      if (normalized.width > 0 && normalized.height > 0) {
        autoSaveCaptureOnCommitRef.current = shouldAutoSaveCaptureOnCommit({
          isFullscreen: Boolean(isFullscreen),
          thumbnailEditMode: Boolean(fullscreenMediaEditingRef.current?.thumbnailEditMode),
          confirmCaptureRegion: confirmCaptureRegionRef.current,
        });
        setCaptureRegionOverlay(normalized);
        setCaptureRegion(normalized);
        if (!isFullscreen || confirmCaptureRegionRef.current) {
          setTimeout(() => {
            setCaptureRegionOverlay(undefined);
          }, 100);
        }
      } else {
        autoSaveCaptureOnCommitRef.current = false;
        setCaptureRegionOverlay(undefined);
        setCaptureRegion(undefined);
        setCapturePreviewSrc(undefined);
      }
    },
    [isFullscreen],
  );

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!captureMode || isFromPortaledChild(e) || captureDraggingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    beginCaptureDrag(e.clientX, e.clientY, e.currentTarget);
  }, [beginCaptureDrag, captureMode]);

  useEffect(() => {
    if (!captureMode) {
      captureDraggingRef.current = false;
      setCaptureDragActive(false);
      setCaptureCursor(null);
      return;
    }
    const onPointerMove = (event: PointerEvent) => {
      const root = mediaDisplayRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      setCaptureCursor(inside || captureDraggingRef.current ? { x, y } : null);
      if (captureDraggingRef.current) {
        updateCaptureDrag(event.clientX, event.clientY, root);
      }
    };
    const onPointerUp = (event: PointerEvent) => {
      const root = mediaDisplayRef.current;
      if (!root || !captureDraggingRef.current) return;
      commitCaptureDrag(event.clientX, event.clientY, root);
    };
    const onDragStart = (event: DragEvent) => {
      event.preventDefault();
    };
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerup', onPointerUp, true);
    document.addEventListener('pointercancel', onPointerUp, true);
    document.addEventListener('dragstart', onDragStart, { capture: true, passive: false });
    return () => {
      document.removeEventListener('pointermove', onPointerMove, true);
      document.removeEventListener('pointerup', onPointerUp, true);
      document.removeEventListener('pointercancel', onPointerUp, true);
      document.removeEventListener('dragstart', onDragStart, true);
    };
  }, [captureMode, commitCaptureDrag, updateCaptureDrag]);

  const onCaptureSurfacePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!captureMode || event.button !== 0) return;
      const root = mediaDisplayRef.current;
      if (!root) return;
      event.preventDefault();
      event.stopPropagation();
      // Pointer capture keeps the drag alive when the cursor crosses the
      // scroll container or leaves the pane; it throws for a pointer id the
      // element cannot capture, which must not abort the selection.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Document-level pointermove/pointerup still drive the drag.
      }
      beginCaptureDrag(event.clientX, event.clientY, root);
    },
    [beginCaptureDrag, captureMode],
  );

  const onCaptureSurfacePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!captureMode || !captureDraggingRef.current) return;
      const root = mediaDisplayRef.current;
      if (!root) return;
      updateCaptureDrag(event.clientX, event.clientY, root);
    },
    [captureMode, updateCaptureDrag],
  );

  const onCaptureSurfacePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const root = mediaDisplayRef.current;
      if (!root || !captureDraggingRef.current) return;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      commitCaptureDrag(event.clientX, event.clientY, root);
    },
    [commitCaptureDrag],
  );

  const onMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!captureMode || isFromPortaledChild(e) || !captureDraggingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      commitCaptureDrag(e.clientX, e.clientY, e.currentTarget);
    },
    [captureMode, commitCaptureDrag],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!captureMode || isFromPortaledChild(e)) return;
      if (captureDraggingRef.current) {
        updateCaptureDrag(e.clientX, e.clientY, e.currentTarget);
      }
    },
    [captureMode, updateCaptureDrag],
  );

  const handlePdfPageChange = useCallback((page: number) => {
    currentPdfPageRef.current = page;
    onViewingPageChange?.(page);
  }, [onViewingPageChange]);

  const handlePdfSearchChange = useCallback((state: PdfSearchHandoffState) => {
    currentPdfSearchRef.current = state.searchQuery.trim() ? state : null;
  }, []);

  const isGoingToRenderSetStillFramePrompt = useMemo(
    () =>
      (isUnifiedSources || isTable) &&
      !hasStillFrameImage(mediaItem) &&
      !isAccordion,
    [
      isUnifiedSources,
      isTable,
      mediaItem?.mediaContext?.stillFrame,
      mediaItem?.mediaContext?.canvas?.stillFrameImage,
      isAccordion,
    ],
  );

  const commitSwitcherChoiceIfNeeded = useCallback(() => {
    const commit = Boolean(
      commitViewChoiceOnCloseRef.current ||
        fullscreenMediaEditingRef.current?.commitViewChoiceOnClose,
    );
    const item = itemToUseRef.current ?? fullscreenMediaItemRef.current ?? mediaItem;
    const choice = displayedMediaChoiceToCommit({
      commitViewChoiceOnClose: commit,
      viewCapture: viewCaptureRef.current,
      savedCapture: resolvePersistedCaptureRef(item) || '',
      stagedCapture: stagedCaptureForCommitRef.current,
      framingCapture: captureModeRef.current,
    });
    if (!choice || !persistDisplayedMediaChoiceRef.current) return false;
    void persistDisplayedMediaChoiceRef.current(choice === 'capture');
    return true;
  }, [mediaItem]);

  const toggleFullScreen = useCallback(() => {
    if (isLinkedMedia) {
      setLinkedMediaItem(null);
      return;
    }
    const newFullScreen = !isFullscreen;
    setFullscreenZoomLevel(0);
    setFullscreenTemporaryRotation(undefined);
    setLocalZoomLevel(0);
    // Keep citation viewing page in session memory before the portal remounts PdfViewer.
    // PdfViewer also writes on every page change, so Escape / setIsFullscreen(false) still restore.
    rememberViewingPageForMedia(
      itemToUseRef.current ?? mediaItem,
      currentPdfPageRef.current,
      viewingPageScope,
    );
    rememberPdfSearchForMedia(
      itemToUseRef.current ?? mediaItem,
      currentPdfSearchRef.current,
      viewingPageScope,
    );
    // Maximize / minimize is a handoff of whatever is selected, not a decision:
    // an explicit Use Full Media / Use Capture has already recorded itself via
    // ``commitViewCapture`` before closing (TR-3019).
    mirrorCaptureViewState(
      getMediaDisplayCaptureIdentityKey(itemToUseRef.current ?? mediaItem),
      viewCaptureRef.current,
      capturePreviewSrcRef.current,
      resolvePersistedCaptureRef(itemToUseRef.current ?? mediaItem),
    );
    if (newFullScreen) {
      committedFullscreenMediaOverride = null;
      if (containerWidth && containerHeight) {
        setNormalContainerDimensions({ width: containerWidth, height: containerHeight });
      } else if (
        mediaDisplayRef.current?.parentElement?.clientWidth &&
        mediaDisplayRef.current?.parentElement?.clientHeight
      ) {
        setNormalContainerDimensions({
          width: mediaDisplayRef.current.parentElement.clientWidth,
          height: mediaDisplayRef.current.parentElement.clientHeight,
        });
      }
      // Do not stamp the viewing page onto mediaContext.pageNumber — that value is
      // part of clip identity. Page retention across the remount uses the handoff above.
      // Fullscreen-only default: beside unless mediaContext already carries an
      // explicit saved position (user gear menu or upstream sourceContext).
      // SourceCard inline uses fallback 'below' at render time without stamping
      // it here, so maximize from answer-item / Evidence Locker opens beside.
      const sourceItem = itemToUseRef.current;
      const effectiveTranscriptPosition = resolveTranscriptPosition({
        mediaContextPosition: sourceItem?.mediaContext?.transcriptPosition,
        fallback: 'side',
      });
      // Stamp layout onto the fullscreen copy only — do not persist via updateMediaContext.
      // Persisting here dirties EventModal (unsaved-changes) on a mere maximize/minimize.
      const mediaItemCopy = {
        ...sourceItem,
        mediaContext: {
          ...sourceItem?.mediaContext,
          transcriptPosition: effectiveTranscriptPosition,
        },
      } as MediaSource;
      stampViewingPageScope(mediaItemCopy, viewingPageScope);
      // Baseline includes remount stamps so exit flush ignores continuity-only layout values.
      setFullscreenLayoutBaseline(
        mediaItemCopy.mediaContext as Record<string, unknown> | undefined,
      );
      setFullscreenMediaItem(mediaItemCopy);
      // Register the obfuscation toggle synchronously on the owner instance so the app-level
      // fullscreen MediaDisplay renders the Obfuscated/Original switcher immediately (the effect
      // below keeps `showObfuscatedPreview` in sync afterwards).
      if (
        isSourceCard &&
        typeof onToggleObfuscatedPreviewRef.current === 'function' &&
        contentModerationTypeRef.current
      ) {
        setFullscreenObfuscation({
          contentModerationType: contentModerationTypeRef.current,
          showObfuscatedPreview: showObfuscatedPreviewRef.current ?? true,
          onToggleObfuscatedPreview: wrappedObfuscationToggle,
        });
      }
      // Register edit callbacks so the app-level fullscreen viewer can trim start/end
      // (owner MediaDisplay returns null while fullscreen and would otherwise drop them).
      if (updateMediaContextRef.current) {
        setFullscreenMediaEditing({
          isEditing: true,
          commitViewChoiceOnClose: commitViewChoiceOnCloseRef.current,
          updateMediaContext: (field: string | object, value?: any) => {
            updateMediaContextRef.current?.(field, value);
            setFullscreenMediaItem((prev) => {
              if (!prev) return prev;
              if (typeof field === 'string') {
                return {
                  ...prev,
                  mediaContext: {
                    ...prev.mediaContext,
                    [field]: value,
                    lastUpdated: Date.now(),
                  },
                };
              }
              return {
                ...prev,
                mediaContext: {
                  ...prev.mediaContext,
                  ...field,
                  lastUpdated: Date.now(),
                },
              };
            });
          },
        });
      } else {
        setFullscreenMediaEditing(null);
      }
    } else {
      // The header switcher only changes the view. Horizontal timeline opts in
      // so closing writes that choice before flush, which would otherwise
      // persist the capture from when the view was maximized (TR-4632).
      // Use Full Media / Use Capture already wrote through the editing bridge;
      // this is a no-op when the saved capture already matches the switcher.
      commitSwitcherChoiceIfNeeded();
      const editedItem = isAppFullscreenViewer
        ? (
            committedFullscreenMediaOverride
            ?? itemToUseRef.current
            ?? mediaItem
            ?? fullscreenMediaItemRef.current
          )
        : (
            committedFullscreenMediaOverride
            ?? fullscreenMediaItemRef.current
            ?? itemToUseRef.current
            ?? mediaItem
          );
      if (isAppFullscreenViewer) {
        flushMediaContextEdits(editedItem);
      } else if (
        editedItem &&
        isFullscreenOwnerMediaDisplay(
          mediaItem,
          editedItem,
          false,
          !!updateMediaContextRef.current,
        )
      ) {
        flushMediaContextEdits(editedItem);
        if (editedItem.mediaContext) {
          setItemToUse((prev) =>
            prev
              ? {
                  ...prev,
                  mediaContext: {
                    ...prev.mediaContext,
                    ...editedItem.mediaContext,
                  },
                }
              : prev,
          );
        }
      }
      setFullscreenMediaItem(null);
      setFullscreenObfuscation(null);
      setFullscreenMediaEditing(null);
      clearFullscreenLayoutBaseline();
    }
    setIsFullscreen(newFullScreen);
  }, [
    isLinkedMedia,
    isFullscreen,
    isSourceCard,
    isAppFullscreenViewer,
    isEventModal,
    containerWidth,
    containerHeight,
    mediaItem,
    setLinkedMediaItem,
    setFullscreenZoomLevel,
    setFullscreenTemporaryRotation,
    setFullscreenMediaItem,
    setIsFullscreen,
    setFullscreenObfuscation,
    setFullscreenMediaEditing,
    wrappedObfuscationToggle,
    flushMediaContextEdits,
    commitSwitcherChoiceIfNeeded,
    viewingPageScope,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      toggleFullScreen,
    }),
    [toggleFullScreen],
  );

  // When fullscreen ends via Escape / setIsFullscreen(false) (not toggleFullScreen),
  // flush trim edits while fullscreenMediaItem is still present, then clear handlers.
  // toggleFullScreen already flushes and nulls fullscreenMediaItem — do not fall back to
  // the owner's itemToUse here or we can overwrite a good flush with stale local state.
  //
  // Every MediaDisplay shares FullScreenContext.isFullscreen, so this effect runs on
  // all instances. Only the app viewer / owning instance may flush and clear the shared
  // layout baseline — a non-owner clear-before-flush would null the baseline and (with
  // the old flush-all-layout fallback) re-apply remount stamps into EventModal.
  const commitSwitcherChoiceIfNeededRef = useRef(commitSwitcherChoiceIfNeeded);
  commitSwitcherChoiceIfNeededRef.current = commitSwitcherChoiceIfNeeded;
  const wasFullscreenRef = useRef(isFullscreen);
  // The app-level viewer unmounts when fullscreen ends, so it never re-renders
  // with isFullscreen false. Escape therefore commits in this cleanup, which
  // runs before the owner's exit flush. X already committed inside toggleFullScreen;
  // a second pass is a no-op once the row matches the switcher.
  useLayoutEffect(() => {
    if (!isAppFullscreenViewer) return;
    return () => {
      commitSwitcherChoiceIfNeededRef.current();
    };
  }, [isAppFullscreenViewer]);
  useEffect(() => {
    const wasFullscreen = wasFullscreenRef.current;
    wasFullscreenRef.current = isFullscreen;
    if (wasFullscreen && !isFullscreen) {
      const editedItem = committedFullscreenMediaOverride ?? fullscreenMediaItemRef.current;
      const isOwner =
        !!editedItem &&
        isFullscreenOwnerMediaDisplay(
          mediaItem,
          editedItem,
          false,
          !!updateMediaContextRef.current,
        );
      if (editedItem) {
        if (isAppFullscreenViewer) {
          flushMediaContextEdits(editedItem);
        } else if (isOwner) {
          flushMediaContextEdits(editedItem);
          if (editedItem.mediaContext) {
            setItemToUse((prev) =>
              prev
                ? {
                    ...prev,
                    mediaContext: {
                      ...prev.mediaContext,
                      ...editedItem.mediaContext,
                    },
                  }
                : prev,
            );
          }
        }
      }
      setFullscreenObfuscation((prev) => (prev ? null : prev));
      setFullscreenMediaEditing((prev) => (prev ? null : prev));
      // Clear only from flusher instances (not every MediaDisplay). Sync is fine:
      // the second flusher may see a null baseline and will skip layout keys.
      if (isAppFullscreenViewer || isOwner) {
        clearFullscreenLayoutBaseline();
        committedFullscreenMediaOverride = null;
      }
    }
  }, [
    isFullscreen,
    isAppFullscreenViewer,
    flushMediaContextEdits,
    setFullscreenObfuscation,
    setFullscreenMediaEditing,
  ]);

  // Keep the app-level fullscreen viewer in sync for content-moderation (CSAM) source cards.
  //
  // The parent remounts the source card when the Obfuscated/Original toggle flips (its key
  // changes), so a per-instance "owner" flag can't survive a toggle. Instead, whichever source
  // card instance currently matches the fullscreen source (by stable source path, which does not
  // change between variants) drives the switcher state and swaps the displayed variant. This is
  // resilient to remounts: after a toggle, the freshly mounted instance re-matches and updates.
  useEffect(() => {
    if (
      !isSourceCard ||
      !isFullscreen ||
      typeof onToggleObfuscatedPreview !== 'function' ||
      !contentModerationType ||
      !mediaItem ||
      !fullscreenMediaItem
    ) {
      return;
    }
    if (mediaSourceStableKey(mediaItem) !== mediaSourceStableKey(fullscreenMediaItem)) return;

    const nextShowObfuscated = showObfuscatedPreview ?? true;
    setFullscreenObfuscation((prev) =>
      prev &&
      prev.contentModerationType === contentModerationType &&
      prev.showObfuscatedPreview === nextShowObfuscated &&
      prev.onToggleObfuscatedPreview === wrappedObfuscationToggle
        ? prev
        : {
            contentModerationType,
            showObfuscatedPreview: nextShowObfuscated,
            onToggleObfuscatedPreview: wrappedObfuscationToggle,
          },
    );

    if (mediaSourceVariantKey(mediaItem) !== mediaSourceVariantKey(fullscreenMediaItem)) {
      const variant = Object.assign({}, mediaItem);
      if (currentPdfPageRef.current && variant.mediaContext) {
        variant.mediaContext = { ...variant.mediaContext, pageNumber: currentPdfPageRef.current };
      }
      setFullscreenMediaItem(variant);
    }
  }, [
    isSourceCard,
    isFullscreen,
    contentModerationType,
    showObfuscatedPreview,
    onToggleObfuscatedPreview,
    wrappedObfuscationToggle,
    mediaItem,
    fullscreenMediaItem,
    setFullscreenObfuscation,
    setFullscreenMediaItem,
  ]);

  useEffect(() => {
    if (!containerWidth) return;
    setLocalContainerWidth(containerWidth);
  }, [containerWidth, imageLoaded]);

  useEffect(() => {
    if (!containerHeight) return;
    setLocalContainerHeight(containerHeight);
  }, [containerHeight]);

  const handleSetTemporaryRotation = useCallback(
    (direction: 'cc' | 'c') => {
      const setRotationState = isFullscreen ? setFullscreenTemporaryRotation : setTemporaryRotation;
      setRotationState((prev) => {
        let currentRotation;
        if (!!updateMediaContext && prev === undefined) {
          currentRotation = mediaItem?.mediaContext?.rotation || 0;
        } else {
          currentRotation = prev ?? 0;
        }
        if (direction === 'cc') {
          let newRotation = currentRotation === 0 ? 270 : currentRotation - 90;
          if (newRotation < 0) {
            newRotation = 270;
          }
          return newRotation;
        }
        let newRotation = currentRotation === 270 ? 0 : currentRotation + 90;
        if (newRotation > 270) {
          newRotation = 0;
        }
        return newRotation;
      });
    },
    [isFullscreen, updateMediaContext, mediaItem?.mediaContext?.rotation, setFullscreenTemporaryRotation, setTemporaryRotation],
  );

  const fullscreenZoomMinRef = useRef(0);

  const zoomIn = useCallback(() => {
    if (isFullscreen) {
      setFullscreenZoomLevel((z) => {
        const next = Math.min(FULLSCREEN_ZOOM_MAX_STEPS, z + 1);
        applyImmediateFullscreenZoomPreview(next, fullscreenSettledZoomRef.current);
        return next;
      });
      return;
    }
    setLocalZoomLevel((z) => Math.min(FULLSCREEN_ZOOM_MAX_STEPS, z + 1));
  }, [isFullscreen, setFullscreenZoomLevel]);

  const zoomOut = useCallback(() => {
    if (isFullscreen) {
      setFullscreenZoomLevel((z) => {
        const next = Math.max(fullscreenZoomMinRef.current, z - 1);
        applyImmediateFullscreenZoomPreview(next, fullscreenSettledZoomRef.current);
        return next;
      });
      return;
    }
    setLocalZoomLevel((z) => Math.max(fullscreenZoomMinRef.current, z - 1));
  }, [isFullscreen, setFullscreenZoomLevel]);

  const setZoomToPercent = useCallback(
    (percent: number) => {
      const next = Math.min(
        FULLSCREEN_ZOOM_MAX_STEPS,
        Math.max(fullscreenZoomMinRef.current, zoomLevelFromPercent(percent)),
      );
      if (isFullscreen) {
        setFullscreenZoomLevel(() => {
          applyImmediateFullscreenZoomPreview(next, fullscreenSettledZoomRef.current);
          return next;
        });
        return;
      }
      setLocalZoomLevel(next);
    },
    [isFullscreen, setFullscreenZoomLevel],
  );

  const onImageLoaded = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleSetRotation = useCallback(
    (direction: 'cc' | 'c') => {
      if (captureModeRef.current || viewCaptureRef.current) return;
      if (!!updateMediaContext) {
        const base = itemToUseRef.current ?? mediaItem;
        if (!base) return;
        const lastUpdated = Date.now();
        let newRotation;
        if (direction === 'cc') {
          newRotation = base.mediaContext?.rotation === 0 ? 270 : (base.mediaContext?.rotation || 0) - 90;
          if (newRotation < 0) {
            newRotation = 270;
          }
        } else {
          newRotation = base.mediaContext?.rotation === 270 ? 0 : (base.mediaContext?.rotation || 0) + 90;
          if (newRotation > 270) {
            newRotation = 0;
          }
        }
        const newMediaContext = { ...base.mediaContext, rotation: newRotation, lastUpdated };
        const newMediaItem = { ...base, mediaContext: newMediaContext };
        updateMediaContext(newMediaContext);
        setItemToUse(newMediaItem);
      } else {
        handleSetTemporaryRotation(direction);
      }
    },
    [updateMediaContext, mediaItem, handleSetTemporaryRotation],
  );

  const handleImageErrors = useCallback((imgError: Record<string, { fileNotFound: boolean }>, mediaItemIn: MediaSource) => {
    const srcToUseIn = getSrcToUse(mediaItemIn);
    if (srcToUseIn) {
      const imgErrorToUse = { ...imgError, fileNotFound: true };
      setImageErrors((prev) => ({ ...prev, [srcToUseIn]: imgErrorToUse }));
    }
  }, []);

  const handleUpdateMediaContext = useCallback(
    (field: string | object, value?: any) => {
      const updater =
        updateMediaContext ??
        (isAppFullscreenViewer ? fullscreenMediaEditing?.updateMediaContext : undefined);
      if (updater) {
        updater(field, value);
      }
    },
    [updateMediaContext, isAppFullscreenViewer, fullscreenMediaEditing?.updateMediaContext],
  );

  const effectiveIsEditing =
    Boolean(isEventModal) ||
    Boolean(isAppFullscreenViewer && fullscreenMediaEditing?.isEditing);

  /**
   * Opened from a Displayed Media card. Starts on the same Full Media | Thumbnail
   * side as that tile (video/audio keep the player; images with a thumbnail show
   * the still) so the user can inspect before choosing New Thumbnail.
   */
  const isThumbnailEditSession =
    Boolean(isAppFullscreenViewer) && Boolean(fullscreenMediaEditing?.thumbnailEditMode);

  useEffect(() => {
    if (!itemToUse) return;
    const fetchAndParseText = async () => {
      if (itemToUse?.mediaType !== 'text' || !itemToUse.srcToUse) return;
      if (isMbox(itemToUse.srcToUse)) return;
      if (isMarkdownPreview(itemToUse)) return;
      if (isPlainTextFile(itemToUse)) return;
      if (isSpreadsheetMediaItem(itemToUse)) return;

      setCanToggleCsv(false);
      if (textRef.current)
        textRef.current.innerText = '';

      try {
        const src = `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(itemToUse.srcToUse)}`;
        const response = await fetch(src);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const text = await response.text();
        if (textRef.current) textRef.current.innerText = text;

        const lines = text.split('\n').filter(l => l.trim()).slice(0, 20);
        if (lines.length >= 3) {
          for (const delim of [',', '\t', '|', ';']) {
            const counts = lines.map(l => l.split(delim).length);
            if (counts[0] >= 2 && counts.every(c => c === counts[0])) {
              setCanToggleCsv(true);
              break;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching text:', error);
        if (textRef.current) textRef.current.innerText = 'Error loading content';
      }
    };

    if (!viewCapture) {
      fetchAndParseText();
    }
  }, [isFullscreen, itemToUse, itemToUse?.srcToUse, viewAsCsvLike, viewCapture]);

  useEffect(() => {
    if (!mediaItem) {
      return;
    }
    const clipSourceItem = getMediaDisplayClipSourceItem(
      mediaItem,
      isFullscreen,
      fullscreenMediaItem,
      isLinkedMedia,
    );
    const clipKey = getMediaDisplayClipIdentityKey(clipSourceItem);
    const loadedItem = itemToUseRef.current;
    const selectionKey = mediaSelectionKey ?? '';
    const mediaSelectionChanged =
      !!selectionKey && selectionKey !== lastMediaSelectionKeyRef.current;
    // Same underlying source (URL + page): sync mediaContext in place so trim-handle
    // edits do not tear down MediaPlayer / exit edit mode.
    if (
      clipKey &&
      clipKey === lastMediaItemClipKeyRef.current &&
      loadedItem &&
      isMediaDisplayClipAligned(loadedItem, clipKey)
    ) {
      const propCtx = mediaItem.mediaContext;
      if (propCtx) {
        setItemToUse((prev) => {
          if (!prev || !isMediaDisplayClipAligned(prev, clipKey)) return prev;
          const localCtx = (prev.mediaContext ?? {}) as Record<string, unknown>;
          const propCtxRecord = propCtx as Record<string, unknown>;
          const nextCtx = mediaSelectionChanged
            ? mergeMediaContextForSegmentSelection(localCtx, propCtxRecord)
            : mergeMediaContextPreservingFreshLocalEdits(localCtx, propCtxRecord);
          // Skip update when trim/context fields are unchanged — holding arrow keys
          // at a clamp boundary used to rewrite lastUpdated and loop setState.
          if (
            localCtx.startTime === nextCtx.startTime &&
            localCtx.endTime === nextCtx.endTime &&
            localCtx.originalStartTime === nextCtx.originalStartTime &&
            localCtx.originalEndTime === nextCtx.originalEndTime &&
            localCtx.rotation === nextCtx.rotation &&
            localCtx.autoScroll === nextCtx.autoScroll &&
            localCtx.showTranscript === nextCtx.showTranscript &&
            localCtx.showTranscriptMarkers === nextCtx.showTranscriptMarkers &&
            localCtx.transcriptPosition === nextCtx.transcriptPosition &&
            localCtx.previewUrl === nextCtx.previewUrl &&
            localCtx.transcriptUrl === nextCtx.transcriptUrl &&
            localCtx.capture === nextCtx.capture &&
            localCtx.stillFrame === nextCtx.stillFrame &&
            mediaContextCanvasEqual(localCtx.canvas, nextCtx.canvas)
          ) {
            return prev;
          }
          return {
            ...prev,
            mediaContext: nextCtx,
          };
        });
      }
      // Fall through to ``checkSourceInfo`` so enrichment / ``fileInfo`` / URL updates
      // still reach ``itemToUse`` when the clip key is unchanged.
    }
    if (selectionKey && (mediaSelectionChanged || clipKey !== lastMediaItemClipKeyRef.current)) {
      lastMediaSelectionKeyRef.current = selectionKey;
    }
    if (clipKey !== lastMediaItemClipKeyRef.current) {
      lastMediaItemClipKeyRef.current = clipKey;
      checkSourceInfoRunRef.current += 1;
      setItemToUse(undefined); // Avoid showing stale content until checkSourceInfo runs
      setPdfPageReady(false);
      capturePersistEpochRef.current += 1;
      setCaptureMode(false);
      setCaptureDragActive(false);
      setCaptureRegion(undefined);
      setCaptureRegionOverlay(undefined);
      setCaptureCursor(null);
      setCapturePreviewSrc(undefined);
      setCapturePreviewError(undefined);
      setCaptureSaveError(undefined);
      captureResultCanvasRef.current = null;
      currentPdfPageRef.current = parseCitationPageFromMedia(clipSourceItem ?? mediaItem);
      handlePdfPageChange(currentPdfPageRef.current ?? 1);
    }
    const checkSourceInfoRunId = checkSourceInfoRunRef.current;
    if (captureFeatureEnabled) {
      const capturedDataObject = parseStagedCaptureFromLocalStorage(
        localStorage.getItem('captureData'),
      );
      setCapturedDataAvailable(capturedDataObject || undefined);
    }
    if (isMbox(mediaItem?.originalUrl) && !mboxMemberAlreadyResolved(mediaItem)) {
      const citedEmailIndex = parseMediaContextEmailIndex(mediaItem?.mediaContext);
      const previewUrl = mediaItem?.mediaContext?.previewUrl || '';
      const currentEmailId = getMboxEmailIdentityKey(mediaItem);

      // Only fetch if this is a different email than what we've already fetched
      if (currentEmailId !== mboxFetchedEmailIdRef.current) {
        // Cancel any previous mbox fetch to prevent stale data from being set
        if (currentMboxFetchRef.current) {
          currentMboxFetchRef.current.abortController.abort();
        }

        // Create a new AbortController for this fetch
        const abortController = new AbortController();
        currentMboxFetchRef.current = { emailId: currentEmailId, abortController };

        const fetchMbox = async () => {
          // Capture the emailId at the start of the fetch to verify it's still current
          const fetchEmailId = currentEmailId;
          try {
            const mboxData = await fetchMboxData(
              mediaItem?.originalUrl || '',
              citedEmailIndex === null ? previewUrl || undefined : undefined,
              { cellId, accessToken },
              abortController.signal,
              citedEmailIndex ?? undefined,
            );

            // Only update state if this fetch is still for the current email
            // This prevents stale data from a previous fetch overwriting current data
            if (currentMboxFetchRef.current?.emailId !== fetchEmailId) {
              return;
            }

            if (mboxData) {
              // Track which email we fetched using the same identifier format
              const fetchedEmailId = mboxData.emailUrl ?
                (() => {
                  const parsedEmailIndex = mboxData.emailUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
                  if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
                    return `${mediaItem?.originalUrl || ''}_${parsedEmailIndex}`;
                  }
                  return mboxData.emailUrl;
                })() :
                currentEmailId;

              // Double-check we're still fetching the current email before updating state
              if (currentMboxFetchRef.current?.emailId === fetchEmailId) {
                mboxFetchedEmailIdRef.current = fetchedEmailId;
                const newMediaItem = {
                  ...mediaItem,
                  mediaType: 'text',
                  mediaUrl: mboxData.emailUrl,
                  previewFilePath: mboxData.emailUrl,
                  mediaContext: {
                    ...mediaItem?.mediaContext,
                    previewUrl: mboxData.emailUrl,
                  },
                } as MediaSource;
                lastMediaItemKeyForViewCaptureRef.current = fetchEmailId;
                setViewCapture(!!newMediaItem.mediaContext?.capture);
                newMediaItem.srcToUse = mboxData.emailUrl;
                setItemToUse(newMediaItem);
              }
            } else {
              // Only update state if this fetch is still for the current email
              if (currentMboxFetchRef.current?.emailId === fetchEmailId) {
                lastMediaItemKeyForViewCaptureRef.current = fetchEmailId;
                setViewCapture(!!mediaItem.mediaContext?.capture);
                const fallbackItem = {
                  ...mediaItem,
                  srcToUse: mediaItem?.mediaContext?.previewUrl || mediaItem?.mediaSrc || mediaItem?.mediaUrl || undefined,
                };
                setItemToUse(fallbackItem);
                // Reset the fetched email ID if fetch failed
                mboxFetchedEmailIdRef.current = null;
              }
            }
          } catch (error) {
            // If aborted, don't update state
            if (error instanceof Error && error.name === 'AbortError') {
              return;
            }
            // Only update state if this fetch is still for the current email
            if (currentMboxFetchRef.current?.emailId === fetchEmailId) {
              lastMediaItemKeyForViewCaptureRef.current = fetchEmailId;
              setViewCapture(!!mediaItem.mediaContext?.capture);
              const fallbackItem = {
                ...mediaItem,
                srcToUse: mediaItem?.mediaContext?.previewUrl || mediaItem?.mediaSrc || mediaItem?.mediaUrl || undefined,
              };
              setItemToUse(fallbackItem);
              // Reset the fetched email ID if fetch failed
              mboxFetchedEmailIdRef.current = null;
            }
          }
        }
        fetchMbox();
      }
    } else {
      // Cancel any pending mbox fetch when switching to non-mbox source
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
      // Reset the fetched email ID when not dealing with mbox
      if (mboxFetchedEmailIdRef.current !== null) {
        mboxFetchedEmailIdRef.current = null;
      }
      const checkSourceInfo = async () => {
        // use the fullscreen media item if in fullScreen, otherwise use the media item
        // Viewing page across regular ↔ fullscreen is owned by PdfViewer memory —
        // do not rewrite mediaContext.pageNumber here (it is clip / citation identity).
        const currentMediaItem = isLinkedMedia && linkedMediaItem ? linkedMediaItem : (isFullscreen ? fullscreenMediaItem : mediaItem);
        if (!!currentMediaItem) {
          // Citation-instance-scoped, matching the handoff: opening a different
          // passage or a later answer's citation re-derives Full Media / Capture
          // instead of inheriting it (TR-3040, TR-3104).
          const mediaItemKey = getMediaDisplayCaptureIdentityKey(currentMediaItem);
          const mediaItemChanged = mediaItemKey !== lastMediaItemKeyForViewCaptureRef.current;
          const viewCaptureForOpen = (item: { mediaType?: string | null; mediaContext?: { capture?: unknown } | null } | null | undefined) => {
            if (!resolvePersistedCaptureRef(item)) return false;
            // Match the compact Displayed Media tile: images with a capture, or
            // video/audio after Use Thumbnail wrote the still-frame poster.
            if (isThumbnailEditSession) {
              return mediaPreviewShowsCapture(item);
            }
            return true;
          };
          const initializeViewCapture = (value: boolean) => {
            if (mediaItemChanged) {
              lastMediaItemKeyForViewCaptureRef.current = mediaItemKey;
              setViewCapture(value);
            }
          };
          try {
            const newMediaItem = { ...currentMediaItem};
            const contentTrimmed = newMediaItem?.content?.trim() || '';
            const fetchUrl = (
              newMediaItem?.mediaSrc ||
              newMediaItem?.mediaUrl ||
              ''
            ).trim();
            const fetchUrlUsableForJson =
              !!fetchUrl &&
              !isArchiveMemberSearchablePath(fetchUrl) &&
              getMediaType(
                { mediaType: 'unknown', mediaUrl: fetchUrl } as MediaSource,
                newMediaItem.fileInfo,
              ) === 'json';
            // Prefer inline citation content when there is no real fetchable JSON file URL
            // (empty, archive-member, or identity/canonical source path only).
            const preferInlineJsonContent =
              jsonMediaDisplayEnabled &&
              newMediaItem?.mediaType === 'json' &&
              !!contentTrimmed &&
              !fetchUrlUsableForJson;
            // Content-only JSON citations carry sourceUrl/sourceFilePath for identity,
            // but must not back-fill those into mediaSrc (wrong getMediaType / JsonViewer URL).
            if (
              !preferInlineJsonContent &&
              (!newMediaItem?.mediaSrc || newMediaItem?.mediaSrc === '') &&
              (!newMediaItem?.mediaUrl || newMediaItem?.mediaUrl === '')
            ) {
              if ((newMediaItem as any)?.previewFilePath && (newMediaItem as any)?.previewFilePath !== '') {
                newMediaItem.mediaSrc = (newMediaItem as any)?.previewFilePath;
              } else if ((newMediaItem as any)?.sourceFilePath && (newMediaItem as any)?.sourceFilePath !== '') {
                newMediaItem.mediaSrc = (newMediaItem as any)?.sourceFilePath;
              } else {
                newMediaItem.mediaSrc = (newMediaItem as any)?.sourceUrl || (newMediaItem as any)?.originalUrl;
              }
            }
            if (preferInlineJsonContent) {
              initializeViewCapture(viewCaptureForOpen(newMediaItem));
              if (checkSourceInfoRunId !== checkSourceInfoRunRef.current) {
                return;
              }
              setItemToUse({
                ...newMediaItem,
                mediaType: 'json',
                mediaSrc: undefined,
                mediaUrl: undefined,
                srcToUse: undefined,
              });
              return;
            }
            if (!(isLinkedMedia || linkedMediaItem) && (newMediaItem?.mediaSrc || newMediaItem?.mediaUrl)) {
              const srcToUseIn = getSrcToUse(newMediaItem);
              const newMediaType = getMediaType(newMediaItem);
              if (newMediaType !== 'unknown') {
                newMediaItem.mediaType = newMediaType;
              }
              initializeViewCapture(viewCaptureForOpen(newMediaItem));
              newMediaItem.srcToUse = srcToUseIn;

              if (checkSourceInfoRunId !== checkSourceInfoRunRef.current) {
                return;
              }
              setItemToUse(newMediaItem);
              if (getMediaType(newMediaItem) === 'pdf' || getMediaType(newMediaItem) === 'docx' || getMediaType(newMediaItem) === 'pptx') {
                setPdfPageReady(true);
              }

              return;
            }
          } catch (error) {
            // just use the original media item
          }
          if (checkSourceInfoRunId !== checkSourceInfoRunRef.current) {
            return;
          }
          initializeViewCapture(viewCaptureForOpen(currentMediaItem));
          const fallbackItem = {
            ...currentMediaItem,
            srcToUse: getSrcToUse(currentMediaItem),
          };
          setItemToUse(fallbackItem);
          if (getMediaType(fallbackItem) === 'pdf' || getMediaType(fallbackItem) === 'docx' || getMediaType(fallbackItem) === 'pptx') {
            setPdfPageReady(true);
          }
        }
      }
      checkSourceInfo();
    }

    // Cleanup function to cancel any pending fetch when component unmounts or effect re-runs
    return () => {
      checkSourceInfoRunRef.current += 1;
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
    };
  }, [
    mediaItem,
    mediaSelectionKey,
    isFullscreen,
    fullscreenMediaItem,
    linkedMediaItem,
    isTable,
    isAccordion,
    isUnifiedSources,
    captureFeatureEnabled,
    jsonMediaDisplayEnabled,
    isThumbnailEditSession,
  ]);

  useEffect(() => {
    if (!mediaItem || !cellId || !accessToken || !itemToUse) {
      return;
    }
    if (isTable || isAccordion || isUnifiedSources) {
      return;
    }

    const clipSourceItem = getMediaDisplayClipSourceItem(
      mediaItem,
      isFullscreen,
      fullscreenMediaItem,
      isLinkedMedia,
    );
    const clipKey = getMediaDisplayClipIdentityKey(clipSourceItem);
    if (clipKey !== lastMediaItemClipKeyRef.current) {
      return;
    }
    if (!isMediaDisplayClipAligned(itemToUse, clipKey)) {
      return;
    }

    const avType = itemToUse.mediaType ?? getMediaType(itemToUse, itemToUse.fileInfo);
    if (avType !== 'video' && avType !== 'audio') {
      return;
    }
    if (resolveMediaTranscriptUrl(itemToUse)) {
      return;
    }

    const enrichmentRunId = ++transcriptEnrichmentRunRef.current;

    void (async () => {
      if (!isMediaDisplayClipAligned(itemToUseRef.current, clipKey)) {
        return;
      }
      const sourceItem = itemToUseRef.current!;
      const lookupUrl = resolveFullS3Url(
        sourceItem,
        sourceItem.srcToUse || sourceItem.mediaSrc || sourceItem.mediaUrl || '',
      );
      if (!lookupUrl) {
        return;
      }

      const fileInfo = await getCurrentFileInfo(lookupUrl, cellId, accessToken);

      if (enrichmentRunId !== transcriptEnrichmentRunRef.current) {
        return;
      }
      if (clipKey !== lastMediaItemClipKeyRef.current) {
        return;
      }

      const latestItem = itemToUseRef.current;
      if (!latestItem || !isMediaDisplayClipAligned(latestItem, clipKey)) {
        return;
      }
      if (resolveMediaTranscriptUrl(latestItem)) {
        return;
      }

      const transcriptUrl = resolveTranscriptUrlFromFileInfo(fileInfo);
      if (!transcriptUrl) {
        return;
      }

      if (enrichmentRunId !== transcriptEnrichmentRunRef.current) {
        return;
      }
      if (clipKey !== lastMediaItemClipKeyRef.current) {
        return;
      }

      setItemToUse({
        ...latestItem,
        fileInfo: fileInfo ?? latestItem.fileInfo,
        transcriptUrl,
        mediaContext: paintTranscriptMediaContext(latestItem.mediaContext, transcriptUrl),
      });
    })();

    return () => {
      transcriptEnrichmentRunRef.current += 1;
    };
  }, [cellId, accessToken, mediaItem, itemToUse, isFullscreen, fullscreenMediaItem, isLinkedMedia, isTable, isAccordion, isUnifiedSources]);

  useEffect(() => {
    if (!itemToUse) return;
    let { width, height } = normalContainerDimensions || {width: mediaDisplayRef.current?.parentElement?.clientWidth, height: mediaDisplayRef.current?.parentElement?.clientHeight};
    if (isTable || isAccordion || isUnifiedSources) {
      width = 116;
      height = itemToUse?.mediaType === 'image' ? 126 : 90;
    }
    if (windowWidth && windowWidth < 450) {
      width = windowWidth * 0.8;
      if (windowHeight) {
        height = windowHeight * 0.5;
      } else {
        height = windowWidth;
      }
    } else if (containerWidth && containerHeight) {
      width = containerWidth;
      height = containerHeight;
    }

    // Legacy shrink only when the parent did not pass an explicit AR/fit box.
    // Applying 0.8 on top of SourceCard containerHeight fights presentationSized layout.
    if (
      !(containerWidth && containerHeight) &&
      sourcesExpanded &&
      (isPresentation || isSourceCard) &&
      !isFullscreen &&
      height
    ) {
      height = height * 0.8;
    }
    setNormalContainerWidth(width);
    setNormalContainerHeight(height);
  }, [
    itemToUse,
    isFullscreen,
    fullscreenMediaItem,
    linkedMediaItem,
    isTable,
    isAccordion,
    isUnifiedSources,
    containerWidth,
    containerHeight,
    windowWidth,
    windowHeight,
    sourcesExpanded,
    isPresentation,
    isSourceCard,
  ]);

  const renderMediaDisplay = () => {
    const propClipKey = propsClipKey;
    const propCaptureKey = propsCitationKey;
    const displayItem =
      itemToUse &&
      lastMediaItemClipKeyRef.current === propClipKey &&
      isMediaDisplayClipAligned(itemToUse, propClipKey)
        ? itemToUse
        : undefined;
    const isThumbnailMode = Boolean(isTable || isAccordion || isUnifiedSources);
    const compactThumbnailItem =
      displayItem ?? (isThumbnailMode ? mediaItem : undefined);
    const persistedCapture = isExplicitlyClearedCapture(mediaItem)
      ? ''
      : resolvePersistedCaptureRef(displayItem, mediaItem);
    const captureIdentityItem = itemToUse ?? displayItem ?? mediaItem;
    const selectedCaseForCapture =
      metaViewerCaseId ??
      (sharedCaseData?.caseId != null ? String(sharedCaseData.caseId).trim() : undefined);
    const stagedForThisItem = stagedCaptureBelongsToMedia(
      capturedDataAvailable,
      captureIdentityItem,
      selectedCaseForCapture,
      propCaptureKey,
    )
      ? capturedDataAvailable
      : undefined;
    const stagedCaptureRef = stagedCaptureImageRef(stagedForThisItem);
    stagedCaptureForCommitRef.current = stagedCaptureRef || '';
    const hasCaptureAvailable =
      Boolean(persistedCapture) ||
      hasStagedCapture(stagedForThisItem) ||
      Boolean(capturePreviewSrc);
    // Gate Remove capture on the capture the pane is actually showing. Reading
    // only ``displayItem.mediaContext`` left the trash disabled while a capture
    // from ``mediaItem`` or staging was on screen (TR-4692).
    const captureRemoval = resolveCaptureRemovalState({
      editingEnabled: Boolean(editingEnabled),
      hasCaptureAvailable,
    });
    const captureDisplaySrc = resolveCaptureDisplaySrc({
      previewSrc: capturePreviewSrc,
      stagedRef: stagedCaptureRef,
      persistedRef: persistedCapture,
      resolveUrl: resolveUserImageUrl,
    });
    const hasPendingCaptureRegion =
      !!captureRegion &&
      captureRegion.width !== undefined &&
      Math.abs(captureRegion.width) > 0 &&
      captureRegion.height !== undefined &&
      Math.abs(captureRegion.height) > 0;
    const canCommitThumbnail =
      captureMode || hasPendingCaptureRegion
        ? Boolean(capturePreviewSrc?.trim())
        : hasCaptureAvailable;
    const captureOwningCaseId = resolveUserImageUploadCaseId({
      selectedCaseId: selectedCaseForCapture,
      media: captureIdentityItem,
      sourceKey: originBindingFromMedia(captureIdentityItem).sourceKey,
    });
    const captureSaveDisabledReason = captureOwningCaseId
      ? undefined
      : 'This capture cannot be saved because the file’s case could not be determined. Select a specific case, or wait for the file to finish loading.';
    const showCsamSafePreview = shouldShowCsamSafePreview({
      showObfuscatedPreview,
      boundSafePreview,
      contentModerationType,
      fileModerationType,
      findingStatus,
      pollPreviewUrl: pollObfuscation.previewUrl,
      obfuscationInProgress: obfuscationOverridesCleared,
    });
    const imageSourceUrl = showCsamSafePreview
      ? boundSafePreview
      : displayItem?.srcToUse;
    const originalImageSrc =
      imageSourceUrl &&
      !isArchiveMemberSearchablePath(imageSourceUrl)
        ? `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(imageSourceUrl)}`
        : '';
    const mediaImageSrc =
      viewCapture && captureDisplaySrc.trim()
        ? captureDisplaySrc
        : originalImageSrc;
    const isCapture = viewCapture && hasCaptureAvailable && Boolean(mediaImageSrc);
    const canCreateCapture =
      captureFeatureEnabled &&
      mediaTypeSupportsThumbnailCapture(displayItem?.mediaType);
    const canChooseCapture = captureFeatureEnabled && hasCaptureAvailable;
    const isFramingCapture = Boolean(
      captureMode || (captureRegionOverlay && !viewCapture),
    );
    const compactThumbMedia = compactThumbnailItem ?? mediaItem;
    // Compact tiles (accordion / Displayed Media / table) may paint a bookmark
    // `canvas.image`. Presentation MediaPane never does — it shows
    // `mediaContext.capture` or the actual document.
    const compactPreview = resolveCompactPreview(compactThumbMedia);
    const compactPreviewSrc = compactPreview.url;
    const allowPresentationCaptureSwitcher =
      Boolean(isPresentation) && !isEventModal && hasCaptureAvailable;
    const captureSwitcherShown =
      !isThumbnailMode &&
      !isHorizontalTimeline &&
      (!isPresentation || isEventModal || isSourceCard || isFullscreen || allowPresentationCaptureSwitcher) &&
      (canCreateCapture || hasCaptureAvailable);
    // Compact tiles always paint the still. Evidence Preview / event modal /
    // fullscreen show Full Media | Capture — Full Media must mount the
    // original (PdfViewer, source image), not the saved or auto-backfilled still.
    const showCompactCaptureStill = shouldShowCompactCaptureStill({
      hasCompactPreview: Boolean(compactPreviewSrc),
      mediaType: compactThumbMedia?.mediaType,
      isThumbnailMode,
      isPresentation: Boolean(isPresentation),
      isSourceCard,
      isFullscreen,
      captureSwitcherShown,
      viewCapture,
    });
    const previewImageSrc = showCompactCaptureStill
      ? compactPreviewSrc
      : mediaImageSrc;
    const showMediaImage =
      !obfuscationProcessing &&
      !obfuscationFailed &&
      Boolean(previewImageSrc) &&
      ((displayItem?.mediaType === 'image' && Boolean(imageSourceUrl)) ||
        (isCapture && !isThumbnailMode) ||
        showCompactCaptureStill);
    // A derived still (capture crop, video still-frame, PDF page canvas) is
    // already rasterized at its final orientation, so rotating it again would
    // double-apply. The source-image fallback still rotates — an Evidence Preview
    // image keeps showCompactCaptureStill true for its whole life, so gating on
    // that flag alone would disable the rotate controls outright (TR-2965).
    //
    // Reads `displayItem` optionally: showCompactCaptureStill paints from
    // `compactThumbMedia` (props) before `displayItem` resolves.
    const suppressImageRotation =
      viewCapture || (showCompactCaptureStill && compactPreview.kind === 'derived-still');
    const { persistedRotation: imagePersistedRotation, temporaryRotation: imageTemporaryRotation } =
      resolveImagePaintRotation({
        suppressRotation: suppressImageRotation,
        temporaryRotation: effectiveTemporaryRotation,
        persistedRotation: displayItem?.mediaContext?.rotation,
      });
    const isRotatable =
      !isTitleSlide &&
      (rotatableMediaTypes.includes(displayItem?.mediaType || '') || isCapture);
    // Evidence Preview (source card) uses the same zoom controls as fullscreen MediaDisplay.
    const zoomUiEnabled = isFullscreen || isSourceCard;
    const isZoomable =
      zoomUiEnabled &&
      isFullscreenZoomableMedia(displayItem, isCapture, viewAsCsvLike, isThumbnailMode);
    const showRotateControls = shouldShowMediaRotateControls({
      isTitleSlide,
      viewingCapture: viewCapture,
      isRotatable,
      captureEligible: canCreateCapture || hasCaptureAvailable,
    });
    const showZoomControls =
      zoomUiEnabled && (isZoomable || canCreateCapture || hasCaptureAvailable);
    fullscreenZoomMinRef.current = getFullscreenMinZoomLevel(zoomUiEnabled, isZoomable);
    const showExpandInToolbar = isLinkedMedia || isFullscreen || showToolbarExpandButton;
    const showContentModerationSwitcher =
      canRevealOriginal &&
      !isThumbnailEditSession &&
      typeof onToggleObfuscatedPreview === 'function';
    const flagFileUrl = resolveFlagFileUrl(displayItem);
    const flagMediaType = displayItem?.mediaType || mediaItem?.mediaType;
    const showFlagMenu =
      canFlagMedia &&
      !isThumbnailEditSession &&
      Boolean(flagFileUrl) &&
      isImageMedia(flagMediaType, flagFileUrl) &&
      shouldShowFlagControl(effectiveContentModerationType);
    const flagMenu = showFlagMenu ? (
      <MediaFlagMenu
        fileUrl={flagFileUrl}
        caseId={metaViewerCaseId}
        mediaType={flagMediaType}
        contentModerationType={effectiveContentModerationType}
        findingStatus={findingStatus}
        manualObfuscationJobStatus={manualObfuscationJobStatus}
        blurFailed={obfuscationFailed}
      />
    ) : null;
    const documentApiPath = junkdrawerDocumentApiPathFromUrl(
      displayItem?.srcToUse,
      displayItem?.mediaType === 'docx'
        ? 'ms-word'
        : displayItem?.mediaType === 'pptx'
          ? 'ms-powerpoint'
          : 'pdf',
    );
    const citationMarkdownExcerpt = resolveCitationMarkdownExcerpt(displayItem?.content);
    const citationMarkdownContent =
      displayItem?.mediaType === 'json' &&
      jsonMediaDisplayEnabled &&
      citationMarkdownExcerpt
        ? enhanceCitationMarkdownContent(citationMarkdownExcerpt, {
            mediaBaseUrl: citationJsonMediaBaseUrl({
              dispatchedFilePath: displayItem?.dispatchedFilePath,
              previewFilePath: displayItem?.previewFilePath,
              mediaUrl: displayItem?.mediaUrl,
              mediaSrc: displayItem?.mediaSrc,
              srcToUse: displayItem?.srcToUse,
              originalUrl: displayItem?.originalUrl,
            }),
          })
        : null;
    const showJsonMarkdownToggle = Boolean(citationMarkdownContent) && !isThumbnailEditSession;
    const jsonMediaUrl =
      (displayItem?.srcToUse ||
        displayItem?.mediaUrl ||
        displayItem?.mediaSrc ||
        '').trim();
    const canShowJsonMedia =
      displayItem?.mediaType === 'json' &&
      jsonMediaDisplayEnabled &&
      (Boolean(jsonMediaUrl) || Boolean(citationMarkdownContent) || Boolean(displayItem?.content?.trim()));
    const showPreviewOnlyToolbar =
      isPreview && (showContentModerationSwitcher || showJsonMarkdownToggle || showFlagMenu);
    const showToolbar =
      !hideMediaChrome &&
      !isTable &&
      !isAccordion &&
      !isUnifiedSources &&
      !isHorizontalTimeline &&
      (showPreviewOnlyToolbar ||
        (!isPreview &&
          (showRotateControls ||
            showZoomControls ||
            showExpandInToolbar ||
            showContentModerationSwitcher ||
            showJsonMarkdownToggle ||
            captureSwitcherShown ||
            showFlagMenu)));
    const sourceUrlForTranscodeNotice =
      displayItem?.sourceFilePath ||
      displayItem?.originalUrl ||
      displayItem?.mediaUrl ||
      displayItem?.mediaSrc ||
      displayItem?.fileInfo?.fileUrl ||
      displayItem?.srcToUse ||
      '';
    const showTranscodedMediaRetryNotice =
      !!displayItem &&
      ['image', 'video', 'audio'].includes(displayItem.mediaType || '') &&
      shouldShowTranscodedMediaRetryNotice(sourceUrlForTranscodeNotice, displayItem.fileInfo);

    const presentationLayout = (isPresentation || isSourceCard) && !isFullscreen;

    // SourceCard / presentation: prefer live props (measured column).
    // ``normalContainer*`` is a fullscreen enter snapshot — do not prefer it here
    // or the player keeps a stale / too-small box after toggle.
    const documentFitWidth = presentationLayout
      ? (containerWidth ?? normalContainerWidth)
      : (normalContainerWidth ?? containerWidth);
    // SourceCard docs/images pass an explicit containerHeight (locked box).
    // Timeline presentation does not — fall back to the measured parent height
    // so PdfViewer can contain-fit (pre-TR-1089).
    const explicitContainerHeight = containerHeight != null && containerHeight > 0;
    const documentFitHeightRaw = presentationLayout
      ? (explicitContainerHeight
          ? containerHeight
          : isPresentation
            ? normalContainerHeight
            : undefined)
      : (normalContainerHeight ?? containerHeight);
    const documentFitHeight =
      documentFitHeightRaw != null
        ? Math.max(
            0,
            documentFitHeightRaw -
              (presentationLayout ? (showToolbar ? 40 : 10) : 0),
          )
        : undefined;
    // Locked fit box only for explicit parent heights (SourceCard docs/images).
    // Timeline presentation and SourceCard video/audio stay content/flex sized.
    const presentationSized =
      presentationLayout
      && explicitContainerHeight
      && !!documentFitWidth
      && documentFitWidth > 0;

    const boundedSpreadsheetViewer = shouldBoundSpreadsheetInFillContainer(
      fillContainer,
      displayItem,
      { viewAsCsvLike },
    );

    // The inline trash control is rendered only when not fullscreen, and this
    // function returns that tree before the fullscreen branch. These helpers
    // have to be initialized on that path: a later ``const`` stays in the
    // temporal dead zone, the click throws, and ``capture: ''`` is never written
    // (TR-4904).
    const clearPendingCapture = () => {
      capturePersistEpochRef.current += 1;
      setCaptureMode(false);
      setCaptureDragActive(false);
      setCaptureRegion(undefined);
      setCaptureRegionOverlay(undefined);
      setCaptureCursor(null);
      setCapturePreviewSrc(undefined);
      setCapturePreviewError(undefined);
      setCaptureSaveError(undefined);
      captureRef.current = undefined;
      captureOverlayRef.current = undefined;
      captureDraggingRef.current = false;
    };

    const persistDisplayedMediaChoice = async (useCapture: boolean): Promise<boolean> => {
      const updater =
        updateMediaContextRef.current ??
        fullscreenMediaEditing?.updateMediaContext;
      const item = itemToUseRef.current ?? displayItem;
      const isAv = item?.mediaType === 'video' || item?.mediaType === 'audio';
      const nextContext = { ...(item?.mediaContext ?? {}) } as Record<string, unknown>;

      if (useCapture) {
        const pending = captureRegion;
        const hasPendingRegion =
          !!pending &&
          pending.width !== undefined &&
          Math.abs(pending.width) > 0 &&
          pending.height !== undefined &&
          Math.abs(pending.height) > 0;
        if (hasPendingRegion) {
          const pendingReady =
            Boolean(capturePreviewSrc?.trim()) || Boolean(captureResultCanvasRef.current);
          if (pendingReady) {
            // Wait for the S3 URI before closing fullscreen. Compact Displayed
            // Media remounts on exit and would otherwise lose the still (TR-2920).
            // Do not also write the preview data URL onto mediaContext.capture.
            return handleCaptureRegion(pending);
          }
        }
        const persistedCapture =
          typeof item?.mediaContext?.capture === 'string'
            ? item.mediaContext.capture.trim()
            : '';
        const imageRef = resolveCapturePersistSrc({
          stagedRef: stagedCaptureImageRef(stagedForThisItem),
          persistedRef: persistedCapture,
        });
        if (imageRef) {
          nextContext.capture = imageRef;
          if (isAv) {
            const video = mediaDisplayRef.current?.querySelector('video');
            const stillLabel =
              video != null
                ? video.currentTime.toFixed(0)
                : item?.mediaContext?.stillFrame;
            nextContext.stillFrame = stillLabel;
            nextContext.canvas = {
              ...((item?.mediaContext?.canvas as Record<string, unknown> | undefined) ?? {}),
              stillFrameImage: imageRef,
              stillFrame: stillLabel,
            };
          } else if (
            item?.mediaType === 'pdf' ||
            item?.mediaType === 'docx' ||
            item?.mediaType === 'pptx'
          ) {
            const pageNumber = parseCitationPageFromMedia(item);
            nextContext.pageNumber = pageNumber;
            nextContext.canvas = {
              ...((item?.mediaContext?.canvas as Record<string, unknown> | undefined) ?? {}),
              image: imageRef,
              pageNumber,
            };
          }
        }
      } else {
        nextContext.capture = '';
        if (isAv) {
          nextContext.stillFrame = '';
          nextContext.canvas = {
            ...((item?.mediaContext?.canvas as Record<string, unknown> | undefined) ?? {}),
            stillFrameImage: '',
            stillFrame: '',
          };
        } else if (
          item?.mediaType === 'pdf' ||
          item?.mediaType === 'docx' ||
          item?.mediaType === 'pptx'
        ) {
          nextContext.canvas = {
            ...((item?.mediaContext?.canvas as Record<string, unknown> | undefined) ?? {}),
            image: '',
          };
        }
      }

      // "Use Full Media" writes an empty capture, which blanks the tile. Logged
      // under the capture-save scope because a clear arriving just after a
      // successful upload looks exactly like the upload never having worked.
      logCaptureSave('info', 'displayed-media choice', {
        useCapture,
        mediaType: item?.mediaType ?? null,
        capture: nextContext.capture ? 'set' : 'cleared',
        hasUpdater: Boolean(updater),
      });

      nextContext.lastUpdated = Date.now();
      updater?.(nextContext);
      if (item) {
        const nextItem: MediaSource = {
          ...item,
          mediaContext: nextContext as MediaSource['mediaContext'],
        };
        itemToUseRef.current = nextItem;
        setItemToUse(nextItem);
        committedFullscreenMediaOverride = nextItem;
      }
      return true;
    };
    persistDisplayedMediaChoiceRef.current = persistDisplayedMediaChoice;

    const mediaRoot = (
      <div
        ref={mediaDisplayRef}
        className={`mediaDisplay relative isolate w-full top-0 z-10 self-stretch ${captureMode && captureCursor ? 'cursor-none [&_*]:cursor-none' : ''}
          ${isFullscreen ? 'flex min-h-0 flex-1 flex-col overflow-hidden h-full w-full' : `${boundedSpreadsheetViewer ? 'h-full min-h-0 min-w-0 max-w-full flex flex-col overflow-hidden' : fillContainer ? 'custom-scrollbar2 overflow-auto' : presentationLayout && !presentationSized ? 'overflow-visible' : 'overflow-hidden'} ${isTable || isUnifiedSources ? 'flex h-full items-center justify-center' : presentationLayout ? (presentationSized || boundedSpreadsheetViewer ? 'flex min-w-0 w-full max-w-full flex-col self-stretch min-h-0 h-full' : isSourceCard ? 'flex min-w-0 w-full max-w-full flex-col self-stretch h-auto min-h-min shrink-0' : 'flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col h-auto self-stretch') : isHorizontalTimeline && !normalContainerHeight ? 'h-auto' : normalContainerHeight ? `h-[calc(100%-10px)] min-h-[${normalContainerHeight-10}px]` : 'h-full'}`}
        `}
        style={
          presentationSized
            ? {
                width: '100%',
                maxWidth: '100%',
                height: documentFitHeightRaw,
                minHeight: documentFitHeightRaw,
              }
            : boundedSpreadsheetViewer
              ? {
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  flex: '1 1 0%',
                  minWidth: 0,
                  minHeight: 0,
                }
              : isEventModal && !isFullscreen && containerWidth && containerHeight
                ? {
                    // Locked event-modal fit box (side vs below transcript sizes).
                    // Width follows the column; cap at the ideal box so the player
                    // scales down inside narrow modals without horizontal scroll.
                    width: '100%',
                    maxWidth: containerWidth,
                    minWidth: 0,
                    height: containerHeight,
                    minHeight: containerHeight,
                  }
                : undefined
        }
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
      >
        {showToolbar && !isFullscreen && (
          <div
            data-testid="media-display-toolbar"
            className={cn(
              'mediaDisplayControls relative z-[10060] box-border flex flex-row bg-[var(--surface-inverse,#0f1d32)] pt-1 pb-2 rounded-t-md px-1 items-center justify-between',
              (isFullscreen || isSourceCard || boundedSpreadsheetViewer) && 'shrink-0',
              (isFullscreen || isSourceCard) && 'p-1 top-0 left-0 w-full',
            )}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className='relative z-[10000] flex flex-row items-center justify-start gap-1'>
              {!isPreview && showRotateControls && (
                <>
                  <TpTooltip
                    content={t('media.rotationUnavailable')}
                    placement="bottom"
                    disabled={isRotatable}
                  >
                    <span className="inline-flex z-[10000]">
                      <Button
                        type="button"
                        {...mediaIconButtonProps}
                        aria-label={t('media.rotateClockwise')}
                        data-testid="media-rotate-clockwise"
                        disabled={!isRotatable || captureMode || viewCapture}
                        onClick={() => handleSetRotation('c')}
                      >
                        <ArrowClockwiseIcon size={20} />
                      </Button>
                    </span>
                  </TpTooltip>
                  <TpTooltip
                    content={t('media.rotationUnavailable')}
                    placement="bottom"
                    disabled={isRotatable}
                  >
                    <span className="inline-flex z-[10000]">
                      <Button
                        type="button"
                        {...mediaIconButtonProps}
                        aria-label={t('media.rotateCounterClockwise')}
                        data-testid="media-rotate-counterclockwise"
                        disabled={!isRotatable || captureMode || viewCapture}
                        onClick={() => handleSetRotation('cc')}
                      >
                        <ArrowCounterClockwiseIcon size={20} />
                      </Button>
                    </span>
                  </TpTooltip>
                </>
              )}
              {!isPreview && showZoomControls && (
                <>
                  <TpTooltip
                    content={t('media.zoomUnavailable')}
                    placement="bottom"
                    disabled={isZoomable}
                  >
                    <span className="inline-flex z-[10000]">
                      <Button
                        type="button"
                        {...mediaIconButtonProps}
                        aria-label={t('media.zoomIn')}
                        data-testid="media-zoom-in"
                        disabled={!isZoomable}
                        onClick={zoomIn}
                      >
                        <MagnifyingGlassPlusIcon size={20} />
                      </Button>
                    </span>
                  </TpTooltip>
                  <TpTooltip
                    content={t('media.zoomUnavailable')}
                    placement="bottom"
                    disabled={isZoomable}
                  >
                    <span className="inline-flex z-[10000]">
                      <Button
                        type="button"
                        {...mediaIconButtonProps}
                        aria-label={t('media.zoomOut')}
                        data-testid="media-zoom-out"
                        disabled={!isZoomable}
                        onClick={zoomOut}
                      >
                        <MagnifyingGlassMinusIcon size={20} />
                      </Button>
                    </span>
                  </TpTooltip>
                </>
              )}
              {!isPreview && !hideCaptureControls && (captureFeatureEnabled || hasCaptureAvailable) && (
                <div className='flex flex-row items-center justify-center gap-1'>
                  {captureSwitcherShown && (
                    <CaptureViewSwitcher
                      viewCapture={viewCapture}
                      hasSavedCapture={hasCaptureAvailable}
                      disabled={captureMode}
                      onChange={setViewCapture}
                    />
                  )}
                  {captureFeatureEnabled &&
                    (!isPresentation || isEventModal || isSourceCard || isFullscreen) &&
                    canCreateCapture && (
                    <TpTooltip content={captureMode ? t('media.captureCancelClick') : t('media.captureCreate')} placement='bottom'>
                    <Button
                      type="button"
                      {...mediaIconButtonProps}
                      aria-label={captureMode ? t('media.cancelCapture') : t('media.newCapture')}
                      onClick={() => {
                        if (captureMode) {
                          capturePersistEpochRef.current += 1;
                          setCaptureRegion(undefined);
                          setCapturePreviewSrc(undefined);
                        }
                        setCaptureMode(prev => !prev);
                      }}
                    >
                      <CropIcon size={20} className={captureMode ? 'text-green-500' : undefined}/>
                    </Button>
                  </TpTooltip>)}
                  {!!updateMediaContext && (canCreateCapture || hasCaptureAvailable) && (
                    <TpTooltip content={!editingEnabled ? t('media.editingDisabled') : hasStagedCapture(stagedForThisItem) ? t('media.captureAddUpdate') : t('media.noCapture')} placement='bottom'>
                      <Button
                        type="button"
                        {...mediaIconButtonProps}
                        disabled={!editingEnabled || !hasStagedCapture(stagedForThisItem)}
                        className="add-capture-button"
                        aria-label={t('media.addOrUpdate')}
                        onClick={() => {
                          const imageRef = stagedCaptureImageRef(stagedForThisItem);
                          if (!imageRef) return;
                          setApplyingCapturedData(true);
                          updateMediaContext('capture', imageRef);
                          setTimeout(() => {
                            setApplyingCapturedData(false);
                            setViewCapture(true);
                          }, 1000);
                        }}
                      >
                        <span className="relative inline-flex items-center">
                          <FrameCornersIcon
                            size={20}
                            className={applyingCapturedData ? 'text-green-500' : 'text-white/70'}
                          />
                          <span className="absolute -right-1 -top-0.5 text-[9px] text-white/70 leading-none">
                            +
                          </span>
                        </span>
                      </Button>
                    </TpTooltip>
                  )}
                  {!!updateMediaContext && (canCreateCapture || hasCaptureAvailable) && (
                    // A disabled <button> emits no pointer events, so the span is what lets
                    // the "why" tooltip open — the icon used to look clickable and say
                    // "Remove the capture for this item" with nothing to remove (TR-4692).
                    // Same wrapper as the rotate / zoom triggers above, and it has to stay
                    // TpTooltip's only child: the tooltip wires aria-describedby through
                    // cloneElement, which it skips for anything but a single element.
                    <TpTooltip content={t(captureRemoval.tooltipKey)} placement='bottom'>
                      <span className="inline-flex z-[10000]">
                        <Button
                          type="button"
                          {...mediaIconButtonProps}
                          disabled={!captureRemoval.enabled}
                          data-testid="media-remove-capture"
                          aria-label={t('media.removeCapture')}
                          onClick={() => {
                            if (!captureRemoval.enabled) return;
                            setRemovingCapturedData(true);
                            commitViewCapture(false);
                            // Drop the in-flight crop and the staged localStorage entry:
                            // either one keeps ``hasCaptureAvailable`` true, so the Capture
                            // side of the switcher re-painted the capture the user had just
                            // removed (TR-4904).
                            clearPendingCapture();
                            void handleUpdateCapture(undefined);
                            // Save an explicit cleared capture — the same marker Use Full
                            // Media writes — instead of ``capture: undefined``, which
                            // JSON.stringify drops so a merging backend kept the old crop,
                            // and which left ``canvas.image`` / ``stillFrameImage`` behind
                            // for the event card to keep showing (TR-4692, TR-4904).
                            void persistDisplayedMediaChoice(false);
                            setTimeout(() => {
                              setRemovingCapturedData(false);
                            }, 1000);
                          }}
                        >
                          <TrashIcon
                            size={20}
                            className={removingCapturedData ? 'text-green-500' : 'text-white/70'}
                          />
                        </Button>
                      </span>
                    </TpTooltip>
                  )}
                </div>
              )}
              {showJsonMarkdownToggle && (
                <div data-testid="json-markdown-toggle">
                  <ChipGroup className="flex-nowrap gap-1">
                    <TpTooltip content={t('media.showMarkdown')} placement="bottom">
                      <Chip
                        type="filter"
                        surface="inverse"
                        size="sm"
                        label={t('media.markdown')}
                        selected={jsonCitationViewMode === 'markdown'}
                        onToggle={() => setJsonCitationViewMode('markdown')}
                      />
                    </TpTooltip>
                    <TpTooltip content={t('media.showJson')} placement="bottom">
                      <Chip
                        type="filter"
                        surface="inverse"
                        size="sm"
                        label={t('media.json')}
                        selected={jsonCitationViewMode === 'json'}
                        onToggle={() => setJsonCitationViewMode('json')}
                      />
                    </TpTooltip>
                  </ChipGroup>
                </div>
              )}
            </div>
            <div className="relative z-[10000] flex flex-row items-center justify-end gap-4">
              {isThumbnailEditSession && (
                <span
                  data-testid="thumbnail-edit-hint"
                  className="hidden text-xs text-white/70 sm:inline"
                >
                  {['audio', 'video'].includes(mediaItem?.mediaType || '')
                    ? t('media.scrubHint')
                    : t('media.newCaptureHint')}
                </span>
              )}
              {flagMenu}
              {showContentModerationSwitcher && (
                <ContentModerationPreviewSwitcher
                  showObfuscatedPreview={showObfuscatedPreview ?? true}
                  onToggle={wrappedObfuscationToggle}
                />
              )}
              {!isPreview && isLinkedMedia ? (
              <Button
                type="button"
                {...mediaIconButtonProps}
                aria-label={t('media.closeLinked')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLinkedMediaItem(null);
                }}
              >
                <XCircleIcon size={20} />
              </Button>
            ) : isThumbnailEditSession ? (
              <Button
                type="button"
                size="sm"
                style="solid"
                color="accent"
                background="inverse"
                id="toggle-fullscreen-button"
                data-testid="thumbnail-edit-done"
                aria-label={t('media.doneEditing')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFullScreen();
                }}
              >
                {t('media.done')}
              </Button>
            ) : !isPreview && (isFullscreen || showToolbarExpandButton) ? (
              <Button
                type="button"
                {...mediaIconButtonProps}
                id="toggle-fullscreen-button"
                aria-label={isFullscreen ? t('media.exitFullscreen') : t('media.enterFullscreen')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFullScreen();
                }}
              >
                {isFullscreen ? <ArrowsInSimpleIcon size={20} data-testid="minimize-button" /> : <ArrowsOutSimpleIcon size={20} data-testid="maximize-button" />}
              </Button>
            ) : null}
            </div>
          </div>
        )}
        {showTranscodedMediaRetryNotice ? (
          <div
            role="note"
            data-testid="transcoded-media-retry-notice"
            className="mb-2 rounded-sm border border-white/15 bg-white/10 px-3 py-2 text-center text-xs leading-snug text-white/80"
          >
            {t('media.retryNotice')}
          </div>
        ) : null}
        {obfuscationProcessing || obfuscationFailed ? (
          <MediaObfuscationPending state={obfuscationState} />
        ) : showMediaImage ? (
          <div
            className={`mediaDisplayImageContainer flex ${
              isTable || isUnifiedSources
                ? 'h-full items-center justify-center'
                : isFullscreen || isSourceCard
                  ? // flex-1 only (no h-full): h-full + toolbar siblings clips the image under controls
                    'min-h-0 flex-1 w-full min-w-0 flex-col items-center justify-center'
                  : 'items-center justify-center'
            } ${isTable || isUnifiedSources ? '' : isFullscreen || isSourceCard ? '' : fillContainer ? 'min-h-0' : presentationLayout ? 'flex-1 min-h-0' : 'h-[calc(100%-30px)]'} w-full`}
            style={
              (isTable || isUnifiedSources) && localContainerWidth
                ? { width: localContainerWidth }
                : presentationSized && documentFitHeight
                  ? { width: '100%', height: documentFitHeight, minHeight: documentFitHeight }
                  : isSourceCard && !isFullscreen
                    ? { width: '100%', minHeight: 240 }
                    : undefined
            }
          >
            {!viewCapture &&
            !showCompactCaptureStill &&
            displayItem &&
            getSrcToUse(displayItem) &&
            imageErrors[getSrcToUse(displayItem) || ''] ? (
              <div className='mediaDisplayImageErrors flex flex-col h-full items-center justify-center w-full break-all wrap'>
                <ImageNotFound
                  id={1}
                  src={powerUserOrg ? `${getSrcToUse(displayItem)}` : `${extractPathAfterBulk(getSrcToUse(displayItem) || '', true).truncatedFilePath}`}
                  isThumbnail={isAccordion || isTable || isUnifiedSources}
                  disableTooltip={isFullscreen || isSourceCard}
                />
              </div>
            ) : isFullscreen ? (
              <FullscreenImageViewport
                persistedRotation={imagePersistedRotation}
                temporaryRotation={imageTemporaryRotation}
                isFullscreen={isFullscreen}
                zoomLevel={zoomLevel}
                src={previewImageSrc}
                alt={
                  displayItem?.srcToUse && !viewCapture
                    ? extractPathAfterBulk(displayItem?.srcToUse, true).truncatedFilePath || t('media.mediaImage')
                    : t('media.mediaImage')
                }
                onLoad={onImageLoaded}
                onError={() => {
                  if (!viewCapture && displayItem?.srcToUse) {
                    handleImageErrors({ [displayItem?.srcToUse]: { fileNotFound: false } }, displayItem);
                  }
                }}
              />
            ) : isSourceCard ? (
              // Evidence Preview: stable object-contain box (no ResizeObserver contain-fit).
              // Zoom grows this box's layout size instead of CSS-scaling the image. A
              // scaled image still measures 100%, so half of what it overflows sits at
              // a negative offset, outside the scrollable region, where no amount of
              // scrolling can reach it. `m-auto` keeps the box centered while it fits
              // and collapses to 0 once it overflows, so the image starts at the
              // top-left content edge; the flex centering it replaces cannot do that,
              // because it re-centers the overflow instead (TR-2408).
              <div className="mediaDisplayImageDiv box-border flex h-full min-h-0 w-full items-start justify-start overflow-auto custom-scrollbar2">
                <div
                  className="box-border m-auto flex shrink-0"
                  style={{
                    width: `${zoomPercentFromLevel(zoomLevel)}%`,
                    height: `${zoomPercentFromLevel(zoomLevel)}%`,
                  }}
                >
                  <img
                    aria-label={t('media.image')}
                    src={previewImageSrc}
                    alt={
                      displayItem?.srcToUse && !viewCapture
                        ? extractPathAfterBulk(displayItem?.srcToUse, true).truncatedFilePath || t('media.mediaImage')
                        : t('media.mediaImage')
                    }
                    className="mediaDisplayImageContent h-full w-full select-none object-contain"
                    style={{
                      transform: `rotate(${imagePaintRotationDeg(
                        imageTemporaryRotation,
                        imagePersistedRotation,
                      )}deg)`,
                      transformOrigin: 'center center',
                    }}
                    onLoad={onImageLoaded}
                    onError={() => {
                      if (!viewCapture && displayItem?.srcToUse) {
                        handleImageErrors({ [displayItem?.srcToUse]: { fileNotFound: false } }, displayItem);
                      }
                    }}
                  />
                </div>
              </div>
            ) : (
              <div
                className={`
                  mediaDisplayImageDiv
                  flex flex-row w-full h-full items-center justify-center
                  ${fillContainer ? 'overflow-visible' : 'overflow-hidden'}
                `}
              >
                <img
                  aria-label={t('media.image')}
                  src={previewImageSrc}
                  alt={showCompactCaptureStill ? t('media.capture') : (displayItem?.srcToUse && !viewCapture ? extractPathAfterBulk(displayItem?.srcToUse, true).truncatedFilePath || t('media.mediaImage') : t('media.mediaImage'))}
                  data-testid={showCompactCaptureStill ? 'displayed-media-thumbnail' : undefined}
                  className={`mediaDisplayImageContent rounded-sm select-none
                    ${isEventModal && !isFullscreen ? 'max-w-full max-h-full w-full h-full' : ''}
                    ${(isPresentation || isSourceCard) && !isFullscreen ? `max-w-full max-h-full` : ''}
                    ${fillContainer && !isFullscreen ? 'max-w-full w-full h-auto object-contain' : ''}
                    ${isAccordion ? `w-[138px] max-h-[178px] overflow-hidden` : ''}
                    ${isTable || isUnifiedSources ? 'max-h-full max-w-full object-contain object-center' : ''}
                    ${isTable ? 'h-full w-full' : ''}
                    ${isUnifiedSources ? 'h-full w-full' : ''}
                    object-contain
                    ${!suppressImageRotation && !isFullscreen && !updateMediaContext && temporaryRotation === 0 ? `transform rotate-0` : ''}
                    ${!suppressImageRotation && !isFullscreen && !updateMediaContext && temporaryRotation === 90 ? `transform rotate-90` : ''}
                    ${!suppressImageRotation && !isFullscreen && !updateMediaContext && temporaryRotation === 180 ? `transform rotate-180` : ''}
                    ${!suppressImageRotation && !isFullscreen && !updateMediaContext && temporaryRotation === 270 ? `transform -rotate-90` : ''}
                    ${!suppressImageRotation && !isFullscreen && compactThumbnailItem?.mediaContext?.rotation === 0 && temporaryRotation === undefined ? `transform rotate-0` : ''}
                    ${!suppressImageRotation && !isFullscreen && compactThumbnailItem?.mediaContext?.rotation === 90 && temporaryRotation === undefined ? `transform rotate-90` : ''}
                    ${!suppressImageRotation && !isFullscreen && compactThumbnailItem?.mediaContext?.rotation === 180 && temporaryRotation === undefined ? `transform rotate-180` : ''}
                    ${!suppressImageRotation && !isFullscreen && compactThumbnailItem?.mediaContext?.rotation === 270 && temporaryRotation === undefined ? `transform -rotate-90` : ''}
                  `}
                  onLoad={onImageLoaded}
                  onError={(e) => {
                    if (!viewCapture && displayItem?.srcToUse) {
                      handleImageErrors({ [displayItem?.srcToUse]: { fileNotFound: false } }, displayItem);
                    }
                  }}
                />
              </div>
            )}
          </div>
        ) : displayItem?.mediaType === 'pdf' && pdfPageReady && !!displayItem?.srcToUse && (displayItem?.srcToUse.trim() !== "") ? (
          <div className={`flex w-full min-h-0 items-center justify-center ${isFullscreen ? 'flex-1 min-w-0' : presentationLayout ? 'min-h-0 flex-1 self-stretch' : 'h-full'}`}>
            <PdfViewer
              media={displayItem}
              updateMediaContext={
                !isPreview && (
                  updateMediaContext ||
                  (isAppFullscreenViewer && fullscreenMediaEditing?.updateMediaContext)
                )
                  ? handleUpdateMediaContext
                  : undefined
              }
              isAccordion={isAccordion}
              isTable={isTable}
              isFullscreen={isFullscreen}
              isEditing={effectiveIsEditing}
              isPresentation={isPresentation || isSourceCard}
              isTitleSlide={isTitleSlide}
              isUnifiedSources={isUnifiedSources}
              hideMediaChrome={hideMediaChrome}
              temporaryRotation={effectiveTemporaryRotation}
              zoomLevel={zoomLevel}
              containerWidth={documentFitWidth}
              containerHeight={documentFitHeight}
              onPageNumberChange={handlePdfPageChange}
              onPdfSearchChange={handlePdfSearchChange}
              viewingPageScope={viewingPageScope}
              page={viewingPage}
              bookmarkAsPreview={isThumbnailEditSession}
            />
          </div>
        ) : displayItem?.mediaType === 'docx' && pdfPageReady && !!displayItem?.srcToUse && (displayItem?.srcToUse.trim() !== "") ? (
          <div className={`flex w-full min-h-0 items-center justify-center ${isFullscreen ? 'flex-1 min-w-0' : presentationLayout ? 'min-h-0 flex-1 self-stretch' : 'h-full'}`}>
            <PdfViewer
              media={displayItem}
              updateMediaContext={
                !isPreview && (
                  updateMediaContext ||
                  (isAppFullscreenViewer && fullscreenMediaEditing?.updateMediaContext)
                )
                  ? handleUpdateMediaContext
                  : undefined
              }
              isAccordion={isAccordion}
              isTable={isTable}
              isFullscreen={isFullscreen}
              isEditing={effectiveIsEditing}
              isPresentation={isPresentation || isSourceCard}
              isTitleSlide={isTitleSlide}
              isUnifiedSources={isUnifiedSources}
              hideMediaChrome={hideMediaChrome}
              temporaryRotation={effectiveTemporaryRotation}
              zoomLevel={zoomLevel}
              containerWidth={documentFitWidth}
              containerHeight={documentFitHeight}
              apiPath={documentApiPath}
              onPageNumberChange={handlePdfPageChange}
              onPdfSearchChange={handlePdfSearchChange}
              viewingPageScope={viewingPageScope}
              page={viewingPage}
              bookmarkAsPreview={isThumbnailEditSession}
            />
          </div>
        ) : displayItem?.mediaType === 'pptx' && pdfPageReady && !!displayItem?.srcToUse && (displayItem?.srcToUse.trim() !== "") ? (
          <div className={`flex w-full min-h-0 items-center justify-center ${isFullscreen ? 'flex-1 min-w-0' : presentationLayout ? 'min-h-0 flex-1 self-stretch' : 'h-full'}`}>
            <PdfViewer
              media={displayItem}
              updateMediaContext={
                !isPreview && (
                  updateMediaContext ||
                  (isAppFullscreenViewer && fullscreenMediaEditing?.updateMediaContext)
                )
                  ? handleUpdateMediaContext
                  : undefined
              }
              isAccordion={isAccordion}
              isTable={isTable}
              isFullscreen={isFullscreen}
              isEditing={effectiveIsEditing}
              isPresentation={isPresentation || isSourceCard}
              isTitleSlide={isTitleSlide}
              isUnifiedSources={isUnifiedSources}
              hideMediaChrome={hideMediaChrome}
              temporaryRotation={effectiveTemporaryRotation}
              zoomLevel={zoomLevel}
              containerWidth={documentFitWidth}
              containerHeight={documentFitHeight}
              apiPath={documentApiPath}
              onPageNumberChange={handlePdfPageChange}
              onPdfSearchChange={handlePdfSearchChange}
              viewingPageScope={viewingPageScope}
              page={viewingPage}
              bookmarkAsPreview={isThumbnailEditSession}
            />
          </div>
        ) : isSpreadsheetMediaItem(displayItem, { viewAsCsvLike }) ? (
          <FullscreenZoomableViewport isFullscreen={isFullscreen} zoomActive={zoomUiEnabled} zoomLevel={zoomLevel} fill>
          <div
            data-testid='xlsx-viewer-container'
            className={`flex w-full min-w-0 max-w-full rounded-md z-[10000] overflow-hidden
              ${boundedSpreadsheetViewer ? 'min-h-0 min-w-0 max-w-full flex-1 flex-col items-stretch self-stretch' : 'flex-row'}
              ${!boundedSpreadsheetViewer && isEventModal && !isFullscreen ? 'bg-gray-900 max-w-full max-h-full items-center justify-center' : ''}
              ${(isPresentation || isSourceCard) && !isFullscreen ? 'min-h-0 flex-1 items-stretch self-stretch' : ''}
              ${!boundedSpreadsheetViewer && !isPresentation && !isSourceCard && !isFullscreen && !isAccordion && !isTable ? 'items-center justify-center' : ''}
              ${isFullscreen ? 'h-full max-h-full w-full max-w-full items-center justify-center' : ''}
              ${!boundedSpreadsheetViewer && isAccordion ? 'overflow-hidden items-center justify-center' : ''}
              ${!boundedSpreadsheetViewer && isTable ? 'items-center justify-center' : ''}
          `}
            style={
              isFullscreen
                ? { height: '100%', maxHeight: '100%', width: '100%', maxWidth: '100%' }
                : boundedSpreadsheetViewer
                  ? { flex: '1 1 0%', minHeight: 0, minWidth: 0, width: '100%', maxWidth: '100%' }
                  : presentationLayout
                    ? { flex: 1, minHeight: 0, minWidth: 0, width: '100%' }
                    : documentFitHeight
                      ? { height: `${documentFitHeight}px` }
                      : undefined
            }
          >
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? (
              <div className="flex items-center justify-center w-full h-full">
                <TableIcon size={25} weight="light" />
              </div>
            ) : themeSpreadsheetEnabled ? (
              <ThemeSpreadsheetViewer
                s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''}
                isFullscreen={isFullscreen}
                zoomLevel={zoomLevel}
                fitParent={boundedSpreadsheetViewer}
                className={boundedSpreadsheetViewer ? 'min-h-0 min-w-0 h-full flex-1' : 'min-w-0'}
              />
            ) : (
              <XlsxViewer
                s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''}
                isFullscreen={isFullscreen}
                zoomLevel={zoomLevel}
                fitParent={boundedSpreadsheetViewer}
                inverseLoadingSurface
                className={boundedSpreadsheetViewer ? 'min-h-0 min-w-0 h-full flex-1' : 'min-w-0'}
              />
            )}
          </div>
          </FullscreenZoomableViewport>
        )
        : canShowJsonMedia ? (
          <FullscreenZoomableViewport isFullscreen={isFullscreen} zoomActive={zoomUiEnabled} zoomLevel={zoomLevel} fill>
          <div
            className={`box-border flex w-full min-w-0 min-h-0 ${
              isFullscreen || presentationLayout
                ? 'min-h-0 flex-1 flex-col self-stretch'
                : 'h-full flex-row items-center justify-center'
            }`}
          >
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? (
              <div className="flex flex-row items-center justify-center h-[90px] w-full">
                <BracketsCurlyIcon className="text-white/65" size={25} />
              </div>
            ) : (
              <div
                className={`box-border w-full max-w-full rounded-sm bg-gray-800 flex flex-col min-h-0 overflow-hidden
                  ${isFullscreen || presentationLayout
                    ? 'min-h-0 flex-1 w-full max-w-full self-stretch'
                    : ''}`}
                style={
                  isFullscreen || presentationLayout
                    ? {
                        flex: 1,
                        minHeight: 0,
                      }
                    : {
                        height: `${normalContainerHeight ? normalContainerHeight - (showToolbar ? 40 : 10) : '400'}px`,
                        maxHeight: `${normalContainerHeight ? normalContainerHeight - (showToolbar ? 40 : 10) : '400'}px`,
                      }
                }
                data-testid="json-media-display"
              >
                {citationMarkdownContent && jsonCitationViewMode === 'markdown' ? (
                  <div className="box-border min-h-0 flex-1 flex flex-col overflow-hidden h-full" data-testid="json-citation-markdown">
                    <AnswerStyleMarkdown
                      content={citationMarkdownContent}
                      className="min-h-0 flex-1 h-full"
                      zoomLevel={zoomLevel}
                      isFullscreen={isFullscreen}
                      allowRawHtml
                      scrollToFirstCitationTarget
                    />
                  </div>
                ) : (jsonMediaUrl || displayItem?.content?.trim()) ? (
                  <div className="box-border min-h-0 flex-1 flex flex-col overflow-hidden h-full" data-testid="json-full-file">
                    <JsonViewer
                      s3Url={jsonMediaUrl || undefined}
                      content={displayItem?.content}
                      className="min-h-0 flex-1 h-full"
                      zoomLevel={zoomLevel}
                      isFullscreen={isFullscreen}
                    />
                  </div>
                ) : (
                  <div className="box-border min-h-0 flex-1 flex items-center justify-center p-4 text-sm text-white/70" data-testid="json-full-file-unavailable">
                    {t('media.jsonUnavailable')}
                  </div>
                )}
              </div>
            )}
          </div>
          </FullscreenZoomableViewport>
        )
        : displayItem?.srcToUse && isMetaFile(displayItem) ? (
          <FullscreenZoomableViewport isFullscreen={isFullscreen} zoomActive={zoomUiEnabled} zoomLevel={zoomLevel}>
          <div className={`flex w-full min-w-0 max-w-full flex-row items-center justify-center overflow-x-hidden ${isFullscreen ? 'h-full min-h-0 max-h-full flex-1' : 'h-full'}`}>
            <MetaFileDisplay
              src={displayItem?.srcToUse || ''}
              mediaItem={displayItem}
              caseId={metaViewerCaseId}
              isFullscreen={isFullscreen}
              isUnifiedSources={isUnifiedSources}
              isAccordion={isAccordion}
              isTable={isTable}
              previewWidthPx={!isFullscreen && documentFitWidth ? documentFitWidth - 70 : undefined}
              previewHeightPx={!isFullscreen && documentFitHeight ? documentFitHeight : undefined}
              hideSocialViewerButton={hideMetaSocialViewerButton}
            />
          </div>
          </FullscreenZoomableViewport>
        ) : (displayItem?.mediaType === 'markdown' || displayItem?.mediaType === 'text') && displayItem?.srcToUse ? (
          <FullscreenZoomableViewport isFullscreen={isFullscreen} zoomActive={zoomUiEnabled} zoomLevel={zoomLevel} fill>
          <div
            className={`box-border flex w-full min-w-0 min-h-0 ${
              isFullscreen || presentationLayout
                ? 'min-h-0 flex-1 flex-col self-stretch'
                : 'h-full flex-row items-center justify-center'
            }`}
          >
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? (
              <div className="flex flex-row items-center justify-center h-[90px] w-full">
                <FileTextIcon className="text-white/65" size={25} />
              </div>
            ) : (() => {
              const plainTextViewer = isPlainTextFile(displayItem);
              // Virtualized Markdown/PlainText viewers use `contain: strict` + padding.
              // Without a definite height they collapse to padding only (p-6 → 48px).
              // SourceCard / presentation must use the measured media column height
              // (same as PDF / MetaFileDisplay), not flex-only sizing.
              const textViewerHeight =
                isFullscreen
                  ? undefined
                  : boundedSpreadsheetViewer
                    ? undefined
                  : documentFitHeight
                    ? `${documentFitHeight}px`
                    : '400px';
              const textViewerWidth = '100%';
              const fillAvailable = isFullscreen || (presentationLayout && !documentFitHeight) || boundedSpreadsheetViewer;
              return (
              <div
                className={`box-border text-black bg-gray-600 rounded-sm
                  ${plainTextViewer ? 'w-full max-w-full' : ''}
                  ${fillAvailable
                    ? 'flex min-h-0 flex-1 w-full max-w-full flex-col overflow-hidden self-stretch'
                    : 'w-full max-w-full overflow-hidden'}`}
                style={
                  fillAvailable
                    ? {
                        width: textViewerWidth,
                        minWidth: textViewerWidth,
                        maxWidth: textViewerWidth,
                        flex: 1,
                        minHeight: 0,
                      }
                    : {
                        height: textViewerHeight,
                        width: textViewerWidth,
                        minWidth: textViewerWidth,
                        maxHeight: textViewerHeight,
                        minHeight: textViewerHeight,
                        maxWidth: textViewerWidth,
                      }
                }
              >
                {displayItem?.mediaType === 'markdown' || isMarkdownPreview(displayItem) ? (
                  <MarkdownViewer
                    s3Url={displayItem?.srcToUse || displayItem?.mediaUrl || displayItem?.mediaSrc || ''}
                    className="min-h-0 h-full w-full flex-1"
                    isFullscreen={isFullscreen}
                    zoomLevel={zoomLevel}
                    scrollToText={
                      isSourceCard
                        ? citationViewDetailsText(
                            displayItem?.content ?? mediaItem?.content,
                          ) || undefined
                        : undefined
                    }
                    scrollToTextKey={isSourceCard ? citationScrollKey : undefined}
                  />
                ) : isSpreadsheetMediaItem(displayItem) || viewAsCsvLike ? (
                  themeSpreadsheetEnabled ? (
                    <ThemeSpreadsheetViewer
                      s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''}
                      isFullscreen={isFullscreen}
                      zoomLevel={zoomLevel}
                      fitParent={boundedSpreadsheetViewer}
                      className={boundedSpreadsheetViewer ? 'min-h-0 min-w-0 h-full flex-1' : 'min-w-0'}
                    />
                  ) : (
                    <XlsxViewer
                      s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''}
                      isFullscreen={isFullscreen}
                      zoomLevel={zoomLevel}
                      fitParent={boundedSpreadsheetViewer}
                      inverseLoadingSurface
                      className={boundedSpreadsheetViewer ? 'min-h-0 min-w-0 h-full flex-1' : 'min-w-0'}
                    />
                  )
                ) : plainTextViewer ? (
                  <PlainTextViewer
                    s3Url={displayItem?.mediaUrl || displayItem?.mediaSrc || displayItem?.srcToUse || ''}
                    className="min-h-0 h-full w-full flex-1"
                    isFullscreen={isFullscreen}
                    zoomLevel={zoomLevel}
                  />
                ) : (
                  <div ref={textRef} className="box-border overflow-y-auto overflow-x-auto custom-scrollbar2 text-black break-all w-full h-full p-2 bg-white">
                  </div>
                )}
              </div>
              );
            })()}
          </div>
          </FullscreenZoomableViewport>
        ) : displayItem?.mediaType === 'website' && displayItem?.srcToUse ? (
          <IframeZoomViewport zoomActive={zoomUiEnabled} zoomLevel={zoomLevel}>
            {themeWebsiteEnabled ? (
              <IframeViewer
                src={displayItem.srcToUse}
                title={displayItem.srcToUse}
                sandbox={null}
                className={`box-border block min-h-[400px] w-full ${localContainerWidth || ''} ${isFullscreen || isSourceCard ? 'h-full' : 'h-64 mt-4'}`}
              />
            ) : (
              <iframe
                src={displayItem?.srcToUse}
                className={`box-border block min-h-[400px] w-full border-0 bg-white ${localContainerWidth || ''} ${isFullscreen || isSourceCard ? 'h-full' : 'h-64 mt-4'}`}
                title={displayItem?.srcToUse}
              />
            )}
          </IframeZoomViewport>
        ) : (displayItem?.mediaType === 'video' || displayItem?.mediaType === 'audio') && displayItem?.srcToUse ? (
          <>
            {hideMediaChrome && isUnifiedSources && displayItem?.mediaType === 'audio' ? (
              compactPreviewSrc ? (
                <img
                  src={compactPreviewSrc}
                  alt=""
                  data-testid="displayed-media-thumbnail"
                  className="size-full object-cover"
                />
              ) : (
                // Displayed Media tile without a still: icon only, no nested plate.
                <div className="flex size-full items-center justify-center">
                  <FileAudioIcon size={32} className="text-white/80" />
                </div>
              )
            ) : (isUnifiedSources || isAccordion || isTable) && !isHorizontalTimeline && displayItem?.mediaType === 'audio' ? (
              compactPreviewSrc ? (
                <img
                  src={compactPreviewSrc}
                  alt=""
                  data-testid="displayed-media-thumbnail"
                  className="size-full object-cover"
                />
              ) : (
                <div className={`box-border m-1 flex flex-col items-center justify-center text-center ${localContainerWidth || 'w-full'} h-[80px] rounded-sm bg-gray-900`}>
                  <div style={{
                    overflow: 'hidden',
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '1.5em'
                  }}>
                    <div
                      style={{
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        width: 'max-content'
                      }}
                      className="text-white/65 text-[11px]"
                    >
                      {(() => {
                        const url = (displayItem.mediaSrc || displayItem.mediaUrl || '').replace(/@/g, '');
                        const filename = url.split('/').pop() || '';
                        const maxLength = 8;
                        const startIndex = Math.max(0, filename.length - maxLength);
                        const truncatedName = filename.length > maxLength
                          ? `...${filename.slice(startIndex, startIndex + maxLength)}`
                          : filename;
                        const startTime = displayItem.mediaContext?.startTime;
                        const startTimeInfo = startTime && startTime !== "0" && startTime !== "0.0"
                          ? ` (${t('sources.rowStart', { time: formatTimeIndex(startTime) })})`
                          : '';
                        return (
                          <>
                            <span>{truncatedName}{startTimeInfo}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <FileAudioIcon size={24} className="text-white/65 mt-1" />
                </div>
              )
            ) : (
              <div
                data-testid="media-player-container"
                id={isFullscreen ? 'media-player-container-fullscreen' : 'media-player-container'}
                className={`box-border flex
                  ${(isPresentation || isSourceCard) ? '' : (isFullscreen ? 'top-0' : '')}
                  ${isFullscreen
                    ? // Fill MediaDisplay under the toolbar; child MediaPlayer is flex-1 so
                      // side transcript can stretch to the full body (not only video height).
                      `min-h-0 flex-1 h-full w-full max-w-full flex-col items-stretch justify-start ${
                        zoomUiEnabled && zoomLevel !== 0
                          ? 'overflow-auto custom-scrollbar2'
                          : 'overflow-hidden'
                      }`
                    : isHorizontalTimeline
                      ? 'w-full max-w-full min-h-0 items-stretch justify-start overflow-hidden'
                    : isTable || isUnifiedSources
                      ? `items-center justify-center h-full ${!isGoingToRenderSetStillFramePrompt || hideMediaChrome ? 'w-full' : ''}${isTable ? ' p-[3px]' : ''}`
                      : presentationLayout
                        ? // Video + below-transcript must grow with drag-resize; only
                          // lock/clip when parent passed an explicit document fit box.
                          `flex w-full max-w-full min-w-0 flex-col items-stretch justify-start self-stretch ${
                            presentationSized
                              ? 'min-h-0 overflow-hidden'
                              : 'h-auto min-h-min shrink-0 overflow-visible'
                          } ${
                            zoomUiEnabled && zoomLevel !== 0
                              ? 'overflow-auto custom-scrollbar2'
                              : ''
                          }`
                      : `w-full h-full max-h-full min-h-0 items-stretch justify-start ${zoomUiEnabled ? 'overflow-auto' : 'overflow-y-auto'} custom-scrollbar2`}
                  ${isGoingToRenderSetStillFramePrompt && !hideMediaChrome ? 'h-[90px] w-[100px] items-center justify-center' : ''}
                `}
                style={
                  (isTable || isUnifiedSources) && containerWidth
                    ? { width: containerWidth }
                    : presentationLayout
                      ? {
                          width: '100%',
                          maxWidth: '100%',
                          // Locked box only when parent passed containerHeight (docs/images).
                          // Otherwise grow with player + transcript (SourceCard expands).
                          height: presentationSized
                            ? (documentFitHeight || 'auto')
                            : 'auto',
                          maxHeight: presentationSized && documentFitHeight
                            ? documentFitHeight
                            : 'none',
                          minHeight: presentationSized ? (documentFitHeight || 0) : 'min-content',
                          flex: presentationSized ? undefined : '0 0 auto',
                          minWidth: 0,
                        }
                      : isEventModal && !isFullscreen
                        ? {
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            minHeight: 0,
                          }
                      : undefined
                }
              >
                <MediaPlayer
                  mediaItem={displayItem}
                  transcriptSrc={
                    isAccordion || isUnifiedSources ? undefined : resolveMediaTranscriptUrl(displayItem)
                  }
                  disableHotkeys={isAccordion || isPresentation || isSourceCard || isUnifiedSources || isHorizontalTimeline || captureMode}
                  showTranscript={isAccordion || isHorizontalTimeline ? false : displayItem.mediaContext?.showTranscript !== undefined ? displayItem.mediaContext?.showTranscript : showTranscript}
                  showTranscriptMarkers={displayItem.mediaContext?.showTranscriptMarkers || false}
                  updateMediaContext={
                    updateMediaContext ||
                    (isAppFullscreenViewer && fullscreenMediaEditing?.updateMediaContext)
                      ? handleUpdateMediaContext
                      : undefined
                  }
                  // Prefer last-saved mediaContext.transcriptPosition across inline ↔ fullscreen.
                  // SourceCard inline (answer-item, Evidence Locker) defaults to below;
                  // fullscreen entry defaults to beside (see toggleFullScreen).
                  transcriptPosition={resolveTranscriptPosition({
                    mediaContextPosition: displayItem.mediaContext?.transcriptPosition,
                    fallback: isSourceCard && !isFullscreen ? 'below' : transcriptPosition,
                  })}
                  useStillFrame={isHorizontalTimeline ? false : (isAccordion || isTable || isUnifiedSources)}
                  isAccordion={isHorizontalTimeline ? false : isAccordion}
                  isHorizontalTimeline={isHorizontalTimeline}
                  isTable={isTable}
                  isEditing={effectiveIsEditing}
                  isUnifiedSources={isUnifiedSources}
                  isFullscreen={isFullscreen}
                  isPresentation={isPresentation || isSourceCard}
                  isEventModal={isEventModal}
                  hideMediaChrome={hideMediaChrome}
                  captureMode={captureMode}
                  temporaryRotation={effectiveTemporaryRotation}
                  sourcesExpanded={sourcesExpanded}
                  zoomLevel={zoomUiEnabled && displayItem.mediaType === 'video' ? zoomLevel : 0}
                  fitWidth={
                    isHorizontalTimeline
                      ? containerWidth
                      : presentationLayout
                      ? documentFitWidth
                      : isEventModal && !isFullscreen
                        ? containerWidth ?? documentFitWidth
                        : undefined
                  }
                  fitHeight={
                    isHorizontalTimeline
                      ? containerHeight
                      : presentationLayout
                      ? documentFitHeight
                      : isEventModal && !isFullscreen && containerHeight
                        ? Math.max(0, containerHeight - (showToolbar ? 40 : 0))
                        : undefined
                  }
                  narrowLayoutWidthAllowancePx={narrowLayoutWidthAllowancePx}
                />
              </div>
            )}
          </>
        ) : displayItem?.mediaType === 'html' && displayItem?.srcToUse ? (
          <>
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? (
              <div
                className={`flex items-center justify-center h-full rounded-md ${
                  hideMediaChrome && isUnifiedSources ? '' : 'bg-gray-600'
                }`}
              >
                <FileHtmlIcon size={25} />
              </div>
            ) : (
              <div
                className={`box-border flex w-full min-w-0 min-h-0 ${
                  isFullscreen || isSourceCard
                    ? 'min-h-0 flex-1 flex-col self-stretch'
                    : 'h-full flex-col'
                }`}
              >
                <IframeZoomViewport zoomActive={zoomUiEnabled} zoomLevel={zoomLevel}>
                  {themeHtmlEnabled ? (
                    <IframeViewer
                      src={`/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(displayItem?.srcToUse || '')}?chunk=${displayItem.mediaContext?.htmlChunk}`}
                      title={displayItem?.srcToUse}
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                      allowFullScreen
                      className={cn(
                        'box-border block h-full min-h-[400px] w-full',
                        isFramingCapture && 'pointer-events-none',
                      )}
                    />
                  ) : (
                    <iframe
                      className={cn(
                        'box-border block h-full min-h-[400px] w-full border-0 bg-white',
                        isFramingCapture && 'pointer-events-none',
                      )}
                      src={`/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(displayItem?.srcToUse || '')}?chunk=${displayItem.mediaContext?.htmlChunk}`}
                      title={displayItem?.srcToUse}
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </IframeZoomViewport>
              </div>
            )}
          </>
        ) : displayItem?.mediaType === 'youtube' && displayItem?.srcToUse ? (
          <FullscreenZoomableViewport isFullscreen={isFullscreen} zoomActive={zoomUiEnabled} zoomLevel={zoomLevel}>
          {themeYoutubeEnabled ? (
            <IframeViewer
              src={`https://www.youtube.com/embed/${(displayItem?.srcToUse || '').split('embed/').pop()}`}
              title={displayItem?.srcToUse}
              sandbox={null}
              allow={YOUTUBE_IFRAME_ALLOW}
              allowFullScreen
              className={`tp-iframe-viewer--embed aspect-video ${localContainerWidth || 'w-full'} ${isFullscreen ? 'h-auto max-h-full max-w-full w-full' : 'h-full'}`}
            />
          ) : (
            <iframe
              className={`aspect-video border-0 ${localContainerWidth || 'w-full'} ${isFullscreen ? 'h-auto max-h-full max-w-full w-full' : 'h-full'}`}
              src={`https://www.youtube.com/embed/${(displayItem?.srcToUse || '').split('embed/').pop()}`}
              title={displayItem?.srcToUse}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
            />
          )}
          </FullscreenZoomableViewport>
        ) : !displayItem ||
          ((displayItem.mediaType === 'pdf' ||
            displayItem.mediaType === 'docx' ||
            displayItem.mediaType === 'pptx') &&
            !pdfPageReady) ? (
          <div
            className={`flex w-full items-center justify-center ${
              isFullscreen ? 'min-h-0 flex-1' : presentationLayout ? 'flex-1 min-h-0' : 'h-full min-h-[120px]'
            }`}
            data-testid="media-display-loading"
          >
            <div className="flex flex-row items-center justify-center gap-2 text-white/80 animate-pulse">
              <SpinnerGapIcon size={25} className="animate-spin-slow" />
              <span className="text-sm">{t('media.loading')}</span>
            </div>
          </div>
        ) : null}
        {captureFeatureEnabled && shouldShowCaptureRegionDialog({
          isFullscreen: Boolean(isFullscreen),
          isThumbnailEditSession,
          confirmCaptureRegion,
        }) && (
          <MediaDisplayCaptureModal
            isOpen={
              !captureRegionOverlay &&
              !!captureRegion &&
              captureRegion.width !== undefined &&
              captureRegion.width > 0 &&
              captureRegion.height !== undefined &&
              captureRegion.height > 0 &&
              captureRegion.x !== undefined &&
              captureRegion.y !== undefined
            }
            captureRegion={captureRegion}
            capModalRef={capModalRef}
            previewSrc={capturePreviewSrc}
            isCapturing={capturePreviewPending}
            captureError={capturePreviewError}
            saveError={captureSaveError}
            saveDisabledReason={captureSaveDisabledReason}
            onClose={() => {
              setCaptureRegion(undefined);
              setCapturePreviewSrc(undefined);
              setCaptureSaveError(undefined);
            }}
            onCancel={() => {
              setCaptureMode(false);
              setCaptureRegion(undefined);
              setCapturePreviewSrc(undefined);
              setCaptureSaveError(undefined);
            }}
            onSave={() => {
              void handleCaptureRegion(captureRegion).then((saved) => {
                if (!saved) return;
                setCaptureMode(false);
                if (isThumbnailEditSession) {
                  // Saving creates the candidate thumbnail; it does not choose it.
                  // Keep the fullscreen editor open on Capture so the user can
                  // compare it with Full Media and explicitly commit either choice
                  // using the footer actions below (TR-3235).
                  commitViewCapture(true);
                }
              });
            }}
          />
        )}
        {captureMode && (
          // Hit layer sits under the inline toolbar (z-[10060]) so crop / rotate /
          // switcher stay clickable outside fullscreen.
          <div
            data-capture-overlay="true"
            data-testid="capture-hit-layer"
            className="absolute inset-0 z-[10040] touch-none"
            onPointerDown={onCaptureSurfacePointerDown}
            onPointerMove={onCaptureSurfacePointerMove}
            onPointerUp={onCaptureSurfacePointerUp}
            onPointerCancel={onCaptureSurfacePointerUp}
            onDragStart={(event) => event.preventDefault()}
          />
        )}
        {captureMode && (
          <MediaDisplayCaptureGuides cursor={captureCursor} />
        )}
        {captureRegionOverlay &&
          (captureRegionOverlay.width ?? 0) > 0 &&
          (captureRegionOverlay.height ?? 0) > 0 && (
            <MediaDisplayCaptureMask region={normalizedCaptureRegion(captureRegionOverlay)} />
          )}
      </div>
    );

    if (!isFullscreen) {
      return mediaRoot;
    }

    const paneTitle = mediaPaneTitleLabel(displayItem);
    const paneTitleIdentity = mediaPaneIdentityPath(displayItem);
    const isDraggingCapture = captureDragActive;
    const captureChromeClass = isFramingCapture ? 'opacity-[0.33]' : undefined;
    const paneIconButtonProps = {
      ...mediaIconButtonProps,
      background: (isFramingCapture ? 'brand' : 'inverse') as typeof mediaIconButtonProps.background,
    };

    return (
      <div
        data-testid="media-display-toolbar"
        className="flex h-full min-h-0 w-full flex-col"
      >
        <Pane
          className="h-full w-full rounded-none"
          overflow="visible"
          bodyClassName="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden bg-[var(--color-ink-090,#0e1c2d)] p-0"
          headerClassName={cn(
            'rounded-none',
            isFramingCapture && 'bg-[var(--surface-brand,#0387fe)] [--tp-pane-header-bg:var(--surface-brand,#0387fe)]',
          )}
          title={
            <span
              className={captureChromeClass}
              data-testid="media-pane-title"
              title={paneTitleIdentity || undefined}
            >
              {paneTitle}
            </span>
          }
          titleActions={
            <div className="flex items-center gap-2">
              {canSeeCsamTags && effectiveContentModerationType ? (
                <CsamDetectionPill type={effectiveContentModerationType} />
              ) : null}
              <div className={cn('flex items-center gap-2', captureChromeClass)}>
                {!isPreview && showZoomControls && (
                  <TpTooltip
                    content={t('media.zoomUnavailable')}
                    placement="bottom"
                    disabled={isZoomable}
                  >
                    <span className="inline-flex">
                      <MediaDisplayZoomControls
                        percent={zoomPercentFromLevel(zoomLevel)}
                        onPercentChange={setZoomToPercent}
                        disabled={isFramingCapture || !isZoomable}
                      />
                    </span>
                  </TpTooltip>
                )}
                {!isPreview && showRotateControls && (
                  <div className="flex items-center gap-0.5">
                    <TpTooltip
                      content={t('media.rotationUnavailable')}
                      placement="bottom"
                      disabled={isRotatable}
                    >
                      <span className="inline-flex">
                        <Button
                          type="button"
                          {...paneIconButtonProps}
                          aria-label={t('media.rotateCounterClockwise')}
                          data-testid="media-rotate-counterclockwise"
                          disabled={isFramingCapture || viewCapture || !isRotatable}
                          onClick={() => handleSetRotation('cc')}
                        >
                          <ArrowCounterClockwiseIcon size={16} />
                        </Button>
                      </span>
                    </TpTooltip>
                    <TpTooltip
                      content={t('media.rotationUnavailable')}
                      placement="bottom"
                      disabled={isRotatable}
                    >
                      <span className="inline-flex">
                        <Button
                          type="button"
                          {...paneIconButtonProps}
                          aria-label={t('media.rotateClockwise')}
                          data-testid="media-rotate-clockwise"
                          disabled={isFramingCapture || viewCapture || !isRotatable}
                          onClick={() => handleSetRotation('c')}
                        >
                          <ArrowClockwiseIcon size={16} />
                        </Button>
                      </span>
                    </TpTooltip>
                  </div>
                )}
              </div>
              {canCreateCapture && !viewCapture && (
                <div
                  className={
                    isFramingCapture
                      ? 'rounded-[var(--radius-rounded-small,4px)] bg-[var(--color-ink-085,#0f1d32)]'
                      : undefined
                  }
                >
                  <Button
                    type="button"
                    size="sm"
                    style="text"
                    color="light"
                    background="inverse"
                    aria-label={
                      isFramingCapture ? t('media.cancelCapture') : t('media.newCapture')
                    }
                    data-testid="create-capture-button"
                    leadingIcon={<CropIcon size={16} />}
                    onClick={() => {
                      if (captureMode) {
                        clearPendingCapture();
                        return;
                      }
                      setCaptureMode(true);
                      setCaptureRegion(undefined);
                      setCaptureRegionOverlay(undefined);
                      setCapturePreviewSrc(undefined);
                      setCapturePreviewError(undefined);
                      setViewCapture(false);
                    }}
                  >
                    {isFramingCapture ? t('media.cancelCaptureLabel') : t('media.newCaptureLabel')}
                  </Button>
                </div>
              )}
            </div>
          }
          headerActions={
            canCreateCapture || hasCaptureAvailable ? (
              <div className={captureChromeClass}>
                <CaptureViewSwitcher
                  viewCapture={viewCapture}
                  hasSavedCapture={hasCaptureAvailable}
                  disabled={captureMode}
                  onChange={(next) => {
                    viewCaptureRef.current = next;
                    setViewCapture(next);
                    if (next) {
                      setCaptureMode(false);
                      setCaptureRegionOverlay(undefined);
                      setCaptureCursor(null);
                    }
                  }}
                />
              </div>
            ) : undefined
          }
          controls={
            <div className={cn('flex items-center gap-1', captureChromeClass)}>
              {showFlagMenu && (
                <MediaFlagMenu
                  fileUrl={flagFileUrl}
                  caseId={metaViewerCaseId}
                  mediaType={flagMediaType}
                  contentModerationType={effectiveContentModerationType}
                  findingStatus={findingStatus}
                  manualObfuscationJobStatus={manualObfuscationJobStatus}
                  blurFailed={obfuscationFailed}
                  compact
                />
              )}
              {showContentModerationSwitcher && (
                <ContentModerationPreviewSwitcher
                  showObfuscatedPreview={showObfuscatedPreview ?? true}
                  onToggle={onToggleObfuscatedPreview}
                />
              )}
              <Button
                type="button"
                {...paneIconButtonProps}
                id="toggle-fullscreen-button"
                data-testid="minimize-button"
                aria-label={t('media.exitFullscreen')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFullScreen();
                }}
              >
                <XIcon size={16} />
              </Button>
            </div>
          }
        >
          {mediaRoot}
          {isThumbnailEditSession && (
            <span data-testid="thumbnail-edit-hint" className="sr-only">
              {['audio', 'video'].includes(mediaItem?.mediaType || '')
                ? t('media.scrubHint')
                : t('media.newCaptureHint')}
            </span>
          )}
          {(canCreateCapture || canChooseCapture) && (
            <MediaDisplayCaptureActions
              isDragging={isDraggingCapture}
              hasCapture={canCommitThumbnail}
              isCaptureMode={isFramingCapture}
              isBusy={capturePreviewPending}
              errorMessage={
                capturePreviewError || captureSaveError || captureSaveDisabledReason
              }
              commitDisabledReason={captureSaveDisabledReason}
              cancelTestId={isThumbnailEditSession ? 'thumbnail-edit-done' : 'capture-pane-cancel'}
              onCancel={() => {
                // Leftover capturePreviewSrc after a successful save is not an
                // in-progress frame — Cancel should exit fullscreen (TR-2927).
                if (captureMode || captureRegion) {
                  clearPendingCapture();
                  setViewCapture(Boolean(displayItem?.mediaContext?.capture));
                  return;
                }
                toggleFullScreen();
              }}
              onUseOriginal={() => {
                void persistDisplayedMediaChoice(false).then(() => {
                  clearPendingCapture();
                  commitViewCapture(false);
                  toggleFullScreen();
                });
              }}
              onUseCapture={() => {
                setCaptureMode(false);
                setCaptureRegionOverlay(undefined);
                setCaptureCursor(null);
                void persistDisplayedMediaChoice(true).then((saved) => {
                  if (!saved) return;
                  commitViewCapture(true);
                  toggleFullScreen();
                });
              }}
            />
          )}
        </Pane>
      </div>
    );
  };

  // Keep hooks above; non-owner instances delegate to the app-level viewer in ``main.tsx``.
  if (isFullscreen && fullscreenMediaItem && !isLinkedMedia && !isAppFullscreenViewer) {
    return null;
  }

  if (isSourceCard && isFullscreen && !isLinkedMedia) {
    return null;
  }

  if (isFullscreenOverlay) {
    const fullscreenOverlay = (
      <div className={`fullScreenMediaDisplayPortal fixed inset-x-0 w-full z-[100000] ${fullscreenOverlayLayoutClass}`}>
        <div id="fullScreenMediaDisplayOuter" className="fullScreenMediaDisplayOuter box-border z-[100000] left-0 top-0 w-full h-full bg-black">
          <div id="fullScreenMediaDisplayInner" className="box-border relative z-[100000] bg-black h-full w-full max-h-full max-w-full
            flex min-h-0 flex-col"
          >
            {renderMediaDisplay()}
          </div>
        </div>
      </div>
    );
    return typeof document !== 'undefined'
      ? createPortal(fullscreenOverlay, document.body)
      : fullscreenOverlay;
  } else {
    return renderMediaDisplay();
  }
});

MediaDisplayComponent.displayName = 'MediaDisplayInner';

const MediaDisplayWithBoundary = forwardRef<MediaDisplayRef, MediaDisplayProps>((props, ref) => {
  const resetKey = getMediaSourceUrlKey(props.mediaItem);
  return (
    <MediaDisplayErrorBoundary resetKey={resetKey}>
      <MediaDisplayComponent ref={ref} {...props} />
    </MediaDisplayErrorBoundary>
  );
});
MediaDisplayWithBoundary.displayName = 'MediaDisplay';

export const MediaDisplay = memo(MediaDisplayWithBoundary);
MediaDisplay.displayName = 'MediaDisplay';
export default MediaDisplay;
